// /* tslint:disable */
/* eslint-disable */

import {HttpClient, HttpHeaders, HttpResponse, HttpResponseBase} from '@angular/common/http';
import {AuthService} from '@auth0/auth0-angular';
import {catchError as _observableCatch, mergeMap as _observableMergeMap} from 'rxjs/operators';
import {
  catchError,
  EMPTY,
  firstValueFrom,
  map,
  Observable,
  ObservableInput,
  ObservedValueOf,
  of as _observableOf,
  of,
  OperatorFunction,
  retry,
  throwError as _observableThrow,
  throwError,
} from 'rxjs';
import {inject, Inject, Injectable, InjectionToken, Optional} from '@angular/core';
import {
  Aangifte,
  AangifteBerekening,
  AangifteRapport,
  AangifteSchenkbelasting,
  BOKKZ_BASE_URL,
  BOKKZ_SERVICE_URL,
  BtwOverzicht,
  CorporateTaxEntity,
  Dividendbelasting,
  IncomeTaxEntity,
  SbBerekening,
  Toeslagen,
  ToeslagenBerekening,
  ValueAddedTaxEntity,
  VpbAangifte,
  VpbBerekening,
} from './api.bokkz.services';
import {JaarrapportageEntity} from './api.bokkz.jaarstukken';
import {MemoriaalBoeking} from './api.bokkz.salaris';
import {ToastService} from '@snelstart/ui-toast';
import {ColumnNumericFilterType, SearchCriteria} from '@snelstart/domain-logic';
import {SnelStartEvent} from '@snelstart/util-events';
import {Store} from '@ngrx/store';
import {Administration as AdministrationState, StateStoreActions} from '@snelstart/util-state-store';

export const SST_BASE_URL = new InjectionToken<string>('SST_BASE_URL');
export const SST_WEBBASE_URL = new InjectionToken<string>('SST_WEBBASE_URL');
export const API_RELATION_MANAGEMENT_BASE_URL = new InjectionToken<string>('API_RELATION_MANAGEMENT_BASE_URL');
export const API_ADMINISTRATION_MANAGEMENT_BASE_URL = new InjectionToken<string>('API_ADMINISTRATION_MANAGEMENT_BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class IbRekenService {

  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_SERVICE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // 'Access-Control-Allow-Headers':'Access-Control-Allow-Origin',
      // 'Accept-Language' : 'nl_NL',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getIncomeTaxCalculation(aangifte: Aangifte): Observable<AangifteBerekening> {
    return this.http
      .post<AangifteBerekening>(this.baseUrl + '/api/v1/ib/bereken/', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getIncomeTaxValidation(aangifte: Aangifte, isVa: boolean): Observable<AangifteBerekening> {
    return this.http.post<AangifteBerekening>(this.baseUrl + `/api/v1/ib/validate/${isVa}`, aangifte, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  getIncomeTaxLoonheffing(aangifte: Aangifte): Observable<Aangifte> {
    return this.http
      .post<Aangifte>(this.baseUrl + '/api/v1/ib/loonheffing/', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getIncomeTaxVoorspelling(aangifte: Aangifte, year: number): Observable<Aangifte> {
    return this.http
      .post<Aangifte>(this.baseUrl + `/api/v1/ib/voorspel/${year}/false`, aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getIncomeTaxBevindingen(controleRequest: ControleRequest): Observable<ControleResponse> {
    return this.http.post<Bevinding[]>(this.baseUrl + '/api/v1/ib/getBevindingen', controleRequest, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  getIncomeTaxUitleg(aangifte: Aangifte): Observable<UitlegResponse> {
    return this.http.post<UitlegResponse>(this.baseUrl + '/api/v1/ib/uitleg/', aangifte, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  getIncomeTaxReport(aangifte: Aangifte): Observable<AangifteRapport> {
    if (aangifte.aangever && aangifte.aangever.bsn != null && aangifte.aangever.geboortedatum != null && aangifte.aangever.burgerlijkeStaat != null) {
      return this.http
        .post<AangifteRapport>(this.baseUrl + '/api/v1/ib/rapport/', aangifte, this.httpOptions)
        .pipe(retry(1), catchError(this.handleError));
    }
    return EMPTY;
  }

  getIncomeTaxOptimalization(aangifte: Aangifte): Observable<Aangifte> {
    return this.http
      .post<Aangifte>(this.baseUrl + '/api/v1/ib/optimaliseer/', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class VpbRekenService {
  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_SERVICE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getCorporateTaxCalculation(aangifte: VpbAangifte): Observable<VpbBerekening> {
    return this.http
      .post<VpbBerekening>(this.baseUrl + '/api/v1/vpb/bereken/', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getCorporateTaxValidation(aangifte: VpbAangifte, isVa: boolean): Observable<VpbBerekening> {
    return this.http.post<VpbBerekening>(this.baseUrl + `/api/v1/vpb/validate/${isVa}`, aangifte, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    // window.alert(errorMessage);
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class DivRekenService {
  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getDividendTaxCalculation(aangifte: Dividendbelasting): Observable<Dividendbelasting> {
    return this.http.post<Dividendbelasting>(this.baseUrl + '/api/v1/db/bereken', aangifte, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  getDividendTaxValidation(aangifte: Dividendbelasting): Observable<Dividendbelasting> {
    return this.http.post<Dividendbelasting>(this.baseUrl + '/api/v1/db/validate', aangifte, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    // window.alert(errorMessage);
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class TslRekenService {
  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getToeslagenCalculation(toeslagen: Toeslagen): Observable<ToeslagenBerekening> {
    return this.http
      .post<ToeslagenBerekening>(this.baseUrl + '/api/v1/toeslagen/bereken/', toeslagen, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getToeslagenIncome(aangifte: Aangifte): Observable<number> {
    return this.http
      .post<number>(this.baseUrl + '/toetsingsinkomen/', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getToeslagen(aangifte: Aangifte): Observable<Toeslagen> {
    return this.http
      .post<Toeslagen>(this.baseUrl + '/api/v1/toeslagen/toeslagen/', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getToeslagenValidation(toeslagen: Toeslagen): Observable<ToeslagenBerekening> {
    return this.http.post<ToeslagenBerekening>(this.baseUrl + '/api/v1/toeslagen/validate/', toeslagen, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class SbRekenService {
  private http: HttpClient;
  private baseUrl: string;
  private serviceUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string,
              @Optional() @Inject(BOKKZ_SERVICE_URL) serviceUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
    this.serviceUrl = serviceUrl !== undefined && serviceUrl !== null ? serviceUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getGiftTaxCalculation(aangifte: AangifteSchenkbelasting): Observable<SbBerekening> {
    return this.http
      .post<SbBerekening>(this.serviceUrl + '/api/v1/sb/bereken', aangifte, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getGiftTaxValidation(aangifte: AangifteSchenkbelasting): Observable<SbBerekening> {
    return this.http.post<SbBerekening>(this.serviceUrl + '/api/v1/sb/validate', aangifte, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class BtwRekenService {

  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_SERVICE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getValueAddedTaxCalculation(overzicht: BtwOverzicht): Observable<BtwOverzicht> {
    return this.http
      .post<BtwOverzicht>(this.baseUrl + '/api/v1/btw/bereken', overzicht, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getValueAddedTaxValidation(overzicht: BtwOverzicht): Observable<BtwOverzicht> {
    return this.http.post<BtwOverzicht>(this.baseUrl + '/api/v1/btw/validate', overzicht, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class PdfGeneratorService {
  // Define API
  // apiURL = "http://localhost:8086/api/v1/pdf/sst";
  apiURL = 'https://prod.leukermakkelijker.nl/api/v1/btw';

  // apiURL = 'https://prod.leukermakkelijker.nl/api/v1/pdf/sst';

  constructor(private http: HttpClient) {
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getValueAddedTaxCalculation(overzicht: BtwOverzicht): Observable<BtwOverzicht> {
    return this.http
      .post<BtwOverzicht>(this.apiURL + '/bereken', overzicht, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getValueAddedTaxValidation(overzicht: BtwOverzicht): Observable<BtwOverzicht> {
    return this.http.post<BtwOverzicht>(this.apiURL + '/validate', overzicht, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

export interface OphaalResponse {
  inhoud: string;
  berichtsoort: string;
}

@Injectable({
  providedIn: 'root',
})
export class BerichtenService {

  private http: HttpClient;
  private baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(BOKKZ_SERVICE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // 'Access-Control-Allow-Headers':'Access-Control-Allow-Origin',
      // 'Accept-Language' : 'nl_NL',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  converteerXbrlToJson(ophaalResponse: OphaalResponse) {
    return this.http
      .post<never>(this.baseUrl + '/api/v1/digipoort/converteer', ophaalResponse, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  getDigipoortEntities(sstRelationId: string, year : string): Observable<DigipoortEntity[]> {
    return this.http
      .get<DigipoortEntity[]>(this.baseUrl + `/api/v1/sst/digipoort/${sstRelationId}/${year}`, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

interface ContactGegevens {
  companyName: string;
  kvkNumber: string;
  contactpersoon: ContactPersoon;
  vestigingsadres: VestigingsAdres;
  factuuradres: string;
}

interface ContactPersoon {
  gender: number;
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
}

export interface DigipoortEntity {
  id?: string;
  sstAdminId?: string;
  sstRelationId?: string;
  verzondenBericht?: string;
  title?: string;
  xbrl?: string;
  submissionDate?: Date;
  incomeTaxEntity: any;
  corporateTaxEntity: any;
}

export interface VestigingsAdres {
  rawAdres: string;
  straatnaam: string;
  huisnummer: number;
  huisnummerToevoeging: string;
  plaats: string;
  postCode: string;
  landCode: string;
  email: string;
  isAfwijkendFactuurAdres: boolean;
}

export interface CompanyInfo {
  id: string;
  name: string;
  administrationId: string;
  isConfirmed: boolean;
  nieuwsbrief: boolean;
  contactgegevens: ContactGegevens;
  abonnement: string;
  betaalgegevens: string;
  heeftRechtOpService: boolean;
  isBetaTestgroepDeelnemer: boolean;
  accountType: number;
}

interface AdministrationSettings {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  mobilePhoneNumber: string;
  fax: string;
  streetName: string;
  streetNumber: string;
  affix: string;
  postalCode: string;
  city: string;
  country: string;
  website: string;
  legalForm: string;
  btwNumber: string;
  identificationNumber: string;
  kvk: string;
  bankNumber: string;
  iban: string;
  bic: string;
  autoGrantAccess: boolean;
}

export interface AdministrationSettingsQueryResult {
  administrationSettings: AdministrationSettings;
}

@Injectable({
  providedIn: 'root',
})
export class SstDataService {
  private baseUrl: string;
  private apiURL: string | undefined;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(SST_WEBBASE_URL) baseUrl?: string,
  ) {
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  getCompanyInfo(): Observable<CompanyInfo> {
    this.apiURL = this.baseUrl + '/MijnSnelStart';
    // https://webapi-tst.snelstart.nl/MijnSnelStart/GetAccountFromCrm
    // https://api-tst.snelstart.nl/MijnSnelStart/GetAccountFromCrm
    return this.http.get<CompanyInfo>(this.apiURL + '/GetAccountFromCrm', this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }

  handleError(error: { error: { message: string }; status: number; message: string }) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class KolommenbalansService {
  private baseSstUrl: string;
  private baseBokkzUrl: string;
  apiURL;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Optional() @Inject(SST_BASE_URL) baseSstUrl?: string,
    @Optional() @Inject(BOKKZ_BASE_URL) baseBokkzUrl?: string,
  ) {
    this.baseSstUrl = baseSstUrl !== undefined && baseSstUrl !== null ? baseSstUrl : '';
    this.baseBokkzUrl = baseBokkzUrl !== undefined && baseBokkzUrl !== null ? baseBokkzUrl : '';
    this.apiURL = this.baseBokkzUrl + '/api/v1/sst';
  }

  httpOptions = {
    headers: new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: 'application/json',
    }),
  };

  async postMemoriaalBoeking(administratieIdentifier: string, memoriaalBoeking: MemoriaalBoeking) {
    let token = '';
    let apiURL = '';
    try {
      if (administratieIdentifier == '0') {
        return;
      }
      token = await this.getAccesstoken(administratieIdentifier);
      apiURL = this.baseSstUrl + '/product/bookkeeping/v1/memoriaalboekingen/create-memoriaalboeking';
      return await firstValueFrom(this.http.post<MemoriaalBoeking>(apiURL, memoriaalBoeking));
    } catch (error) {
      const err = error as HttpError;
      throw err;
    }
  }

  async getDagboekId(administratieIdentifier: string) {
    let token = '';
    let apiURL = '';
    try {
      if (administratieIdentifier == '0') {
        return;
      }
      token = await this.getAccesstoken(administratieIdentifier);
      apiURL = this.baseSstUrl + '/product/bookkeeping/v1/ledger/get-dagboeken';
      return await firstValueFrom(
        this.http.get<DagBoekType[]>(
          apiURL,
          // httpOptionsWithAuth
        ),
      );
    } catch (error) {
      console.error('url      ', JSON.stringify(apiURL));
      console.error('token    ', JSON.stringify(token));
      throw error as HttpError;
    }
  }

  async getGrootboekenIds(administratieIdentifier: string) {
    let token = '';
    let apiURL = '';
    try {
      if (administratieIdentifier == '0') {
        return;
      }
      token = await this.getAccesstoken(administratieIdentifier);
      apiURL = this.baseSstUrl + '/product/bookkeeping/v1/ledger/search-grootboeken?skip=0&pageSize=9999';
      return await firstValueFrom(this.http.get<GrootboekIdentifierResponse>(apiURL));
    } catch (error) {
      console.error('url      ', JSON.stringify(apiURL));
      console.error('token    ', JSON.stringify(token));
      throw error as HttpError;
    }
  }

  private store = inject(Store);

  async getAccesstoken(administratieIdentifier: string) {
    try {
      this.store.dispatch(
        StateStoreActions.setAdministration({
          identifier: administratieIdentifier,
          name: undefined,
        } satisfies AdministrationState),
      );
      return await firstValueFrom(
        this.authService.getAccessTokenSilently({
          cacheMode: 'off',
          authorizationParams: {
            administrationId: administratieIdentifier,
          },
        }),
      );
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  }

  async getUpdatedAangifteMetAdministratie(
    administratieIdentifier: string,
    clientId: string,
    aangifte: CorporateTaxEntity | IncomeTaxEntity | ValueAddedTaxEntity | JaarrapportageEntity,
    partnerBoolean?: boolean,
    primaryAdminId?: string,
  ) {
    try {
      if (administratieIdentifier == '0') {
        return aangifte;
      }
      const token = await this.getAccesstoken(administratieIdentifier);
      const bsnIb = partnerBoolean ? aangifte.aangifte?.partnerAangifte.aangever?.bsn : aangifte.aangifte?.aangever?.bsn;
      const bsn = bsnIb || aangifte.aangifte?.identificatie?.fiscaalNummer || aangifte.aangifte?.omzetbelastingNummer;
      return await firstValueFrom(
        this.http.post<IncomeTaxEntity | CorporateTaxEntity | ValueAddedTaxEntity | JaarrapportageEntity>(
          this.apiURL + '/balans/' + clientId + '/0',
          {token: token, bsn: bsn, aangifte: JSON.stringify(aangifte)},
          this.httpOptions,
        ),
      );
    } catch (error) {
      console.error('Error getting enriched tax form:', error);
      throw error;
    } finally {
      if (primaryAdminId) {
        const token = await this.getAccesstoken(primaryAdminId);
        console.log('Reset:', token);
      }
    }
  }

  getAdministraties(): Observable<AdministratieApiSst[]> {
    const administratieLijstUrl =
      this.baseSstUrl + '/operations/administration-management/v1/accountancy-dashboard-query/accountancy-dashboards';
    return this.http.get<AdministratieApiSst[]>(administratieLijstUrl, this.httpOptions).pipe(
      map((data) => {
        return data;
      }),
      catchError((error) => {
        return of(error.error);
      }),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class CustomerClient {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_RELATION_MANAGEMENT_BASE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl ?? '/product/relation-management';
  }

  /**
   * @param body (optional)
   * @return Created
   */
  createCustomer(body?: CustomerRequest | undefined): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer/create-customer';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreateCustomer(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreateCustomer(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processCreateCustomer(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 201) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result201: any = null;
          let resultData201 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result201 = CustomerResponse.fromJS(resultData201);
          return _observableOf(result201);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = ProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result500: any = null;
          let resultData500 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result500 = ProblemDetails.fromJS(resultData500);
          return throwException('Internal Server Error', status, _responseText, _headers, result500);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return OK
   */
  updateCustomer(body?: CustomerRequest | undefined): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer/update-customer';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('put', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdateCustomer(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdateCustomer(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processUpdateCustomer(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomerResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = ProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result500: any = null;
          let resultData500 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result500 = ProblemDetails.fromJS(resultData500);
          return throwException('Internal Server Error', status, _responseText, _headers, result500);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  archiveCustomer(id: string): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer/archive-customer/{id}';
    if (id === undefined || id === null) throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processArchiveCustomer(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processArchiveCustomer(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processArchiveCustomer(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomerResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = ProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result500: any = null;
          let resultData500 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result500 = ProblemDetails.fromJS(resultData500);
          return throwException('Internal Server Error', status, _responseText, _headers, result500);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  deArchiveCustomer(id: string): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer/de-archive-customer/{id}';
    if (id === undefined || id === null) throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDeArchiveCustomer(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDeArchiveCustomer(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processDeArchiveCustomer(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomerResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = ProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result500: any = null;
          let resultData500 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result500 = ProblemDetails.fromJS(resultData500);
          return throwException('Internal Server Error', status, _responseText, _headers, result500);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  deleteCustomer(id: string): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer/delete-customer/{id}';
    if (id === undefined || id === null) throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('delete', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDeleteCustomer(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDeleteCustomer(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processDeleteCustomer(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomerResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = ProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result500: any = null;
          let resultData500 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result500 = ProblemDetails.fromJS(resultData500);
          return throwException('Internal Server Error', status, _responseText, _headers, result500);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }
}

@Injectable({
  providedIn: 'root',
})
export class AdministrationQueryClient {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_ADMINISTRATION_MANAGEMENT_BASE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl ?? '';
  }

  /**
   * @return OK
   * @deprecated
   */
  administrations(): Observable<AdministrationResultWithAvailableModuleActionsOld[]> {
    let url_ = this.baseUrl + '/v1/administration-query/administrations';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAdministrations(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAdministrations(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AdministrationResultWithAvailableModuleActionsOld[]>;
            }
          } else return _observableThrow(response_) as any as Observable<AdministrationResultWithAvailableModuleActionsOld[]>;
        }),
      );
  }

  protected processAdministrations(response: HttpResponseBase): Observable<AdministrationResultWithAvailableModuleActionsOld[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(AdministrationResultWithAvailableModuleActionsOld.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return OK
   */
  search(body?: AdministrationsQueryRequest | undefined): Observable<AdministrationsQueryResponse> {
    let url_ = this.baseUrl + '/v1/administration-query/search';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSearch(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSearch(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AdministrationsQueryResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AdministrationsQueryResponse>;
        }),
      );
  }

  protected processSearch(response: HttpResponseBase): Observable<AdministrationsQueryResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdministrationsQueryResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = DomainProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return OK
   */
  administrationNameSearch(body?: AdministrationNamesQueryRequest | undefined): Observable<AdministrationNamesQueryResponse> {
    let url_ = this.baseUrl + '/v1/administration-query/administration-name-search';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAdministrationNameSearch(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAdministrationNameSearch(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AdministrationNamesQueryResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AdministrationNamesQueryResponse>;
        }),
      );
  }

  protected processAdministrationNameSearch(response: HttpResponseBase): Observable<AdministrationNamesQueryResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdministrationNamesQueryResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = DomainProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return OK
   */
  administrationOwnerSearch(body?: AdministrationOwnersQueryRequest | undefined): Observable<AdministrationOwnersQueryResponse> {
    let url_ = this.baseUrl + '/v1/administration-query/administration-owner-search';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAdministrationOwnerSearch(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAdministrationOwnerSearch(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<AdministrationOwnersQueryResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<AdministrationOwnersQueryResponse>;
        }),
      );
  }

  protected processAdministrationOwnerSearch(response: HttpResponseBase): Observable<AdministrationOwnersQueryResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdministrationOwnersQueryResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 422) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result422: any = null;
          let resultData422 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result422 = DomainProblemDetails.fromJS(resultData422);
          return throwException('Unprocessable Content', status, _responseText, _headers, result422);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param permissions (optional)
   * @return OK
   */
  availableAdministrationPermissions(permissions?: PermissionType[] | undefined): Observable<PermissionStateModel[]> {
    let url_ = this.baseUrl + '/v1/administration-query/available-administration-permissions?';
    if (permissions === null) throw new Error("The parameter 'permissions' cannot be null.");
    else if (permissions !== undefined)
      permissions &&
      permissions.forEach((item) => {
        url_ += 'permissions=' + encodeURIComponent('' + item) + '&';
      });
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processAvailableAdministrationPermissions(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processAvailableAdministrationPermissions(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<PermissionStateModel[]>;
            }
          } else return _observableThrow(response_) as any as Observable<PermissionStateModel[]>;
        }),
      );
  }

  protected processAvailableAdministrationPermissions(response: HttpResponseBase): Observable<PermissionStateModel[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(PermissionStateModel.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  shelvedAdministrations(): Observable<ShelvedAdministrationResult[]> {
    let url_ = this.baseUrl + '/v1/administration-query/shelved-administrations';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processShelvedAdministrations(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processShelvedAdministrations(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ShelvedAdministrationResult[]>;
            }
          } else return _observableThrow(response_) as any as Observable<ShelvedAdministrationResult[]>;
        }),
      );
  }

  protected processShelvedAdministrations(response: HttpResponseBase): Observable<ShelvedAdministrationResult[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(ShelvedAdministrationResult.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  excludedOutdatedAdministrationsOnTest(): Observable<OutdatedAdministrationResult[]> {
    let url_ = this.baseUrl + '/v1/administration-query/excluded-outdated-administrations-on-test';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processExcludedOutdatedAdministrationsOnTest(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processExcludedOutdatedAdministrationsOnTest(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<OutdatedAdministrationResult[]>;
            }
          } else return _observableThrow(response_) as any as Observable<OutdatedAdministrationResult[]>;
        }),
      );
  }

  protected processExcludedOutdatedAdministrationsOnTest(response: HttpResponseBase): Observable<OutdatedAdministrationResult[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(OutdatedAdministrationResult.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return OK
   */
  outdatedAdministrations(body?: OutdatedAdministrationsQueryRequest | undefined): Observable<OutdatedAdministrationsQueryResponse> {
    let url_ = this.baseUrl + '/v1/administration-query/outdated-administrations';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processOutdatedAdministrations(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processOutdatedAdministrations(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<OutdatedAdministrationsQueryResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<OutdatedAdministrationsQueryResponse>;
        }),
      );
  }

  protected processOutdatedAdministrations(response: HttpResponseBase): Observable<OutdatedAdministrationsQueryResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = OutdatedAdministrationsQueryResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  outdatedPendingRemovedAdministrations(): Observable<OutdatedAdministrationResult[]> {
    let url_ = this.baseUrl + '/v1/administration-query/outdated-pending-removed-administrations';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processOutdatedPendingRemovedAdministrations(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processOutdatedPendingRemovedAdministrations(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<OutdatedAdministrationResult[]>;
            }
          } else return _observableThrow(response_) as any as Observable<OutdatedAdministrationResult[]>;
        }),
      );
  }

  protected processOutdatedPendingRemovedAdministrations(response: HttpResponseBase): Observable<OutdatedAdministrationResult[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(OutdatedAdministrationResult.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  ghostAdministrations(): Observable<GhostAdministrationResult[]> {
    let url_ = this.baseUrl + '/v1/administration-query/ghost-administrations';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGhostAdministrations(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGhostAdministrations(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<GhostAdministrationResult[]>;
            }
          } else return _observableThrow(response_) as any as Observable<GhostAdministrationResult[]>;
        }),
      );
  }

  protected processGhostAdministrations(response: HttpResponseBase): Observable<GhostAdministrationResult[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(GhostAdministrationResult.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  zombieAdministrations(): Observable<ZombieAdministrationResult[]> {
    let url_ = this.baseUrl + '/v1/administration-query/zombie-administrations';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'text/plain',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processZombieAdministrations(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processZombieAdministrations(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ZombieAdministrationResult[]>;
            }
          } else return _observableThrow(response_) as any as Observable<ZombieAdministrationResult[]>;
        }),
      );
  }

  protected processZombieAdministrations(response: HttpResponseBase): Observable<ZombieAdministrationResult[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(ZombieAdministrationResult.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }
}

@Injectable({
  providedIn: 'root',
})
export class CustomerQueryClient {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_RELATION_MANAGEMENT_BASE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl ?? '/product/relation-management';
  }

  /**
   * @param body (optional)
   * @return OK
   */
  getPaginated(body?: RelationColumnPaginationRequest | undefined): Observable<RelationSearchResults> {
    let url_ = this.baseUrl + '/v2/customer-query/get-paginated';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('post', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetPaginated(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetPaginated(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<RelationSearchResults>;
            }
          } else return _observableThrow(response_) as any as Observable<RelationSearchResults>;
        }),
      );
  }

  protected processGetPaginated(response: HttpResponseBase): Observable<RelationSearchResults> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = RelationSearchResults.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param maxResults (optional)
   * @return OK
   */
  getDistinctValues(column: RelationColumn, maxResults?: number | undefined): Observable<DistinctValuesResponse> {
    let url_ = this.baseUrl + '/v2/customer-query/get-distinct-values?';
    if (column === undefined || column === null)
      throw new globalThis.Error("The parameter 'column' must be defined and cannot be null.");
    else url_ += 'column=' + encodeURIComponent('' + column) + '&';
    if (maxResults === null) throw new globalThis.Error("The parameter 'maxResults' cannot be null.");
    else if (maxResults !== undefined) url_ += 'maxResults=' + encodeURIComponent('' + maxResults) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetDistinctValues(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetDistinctValues(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<DistinctValuesResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<DistinctValuesResponse>;
        }),
      );
  }

  protected processGetDistinctValues(response: HttpResponseBase): Observable<DistinctValuesResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = DistinctValuesResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param searchTerm (optional)
   * @param orderBy (optional)
   * @param orderByDescending (optional)
   * @param page (optional)
   * @param pageSize (optional)
   * @param includeReserved (optional)
   * @param onlyArchived (optional)
   * @param includeArchived (optional)
   * @return OK
   */
  searchAllCustomers(
    searchTerm?: string | undefined,
    orderBy?: string | undefined,
    orderByDescending?: boolean | undefined,
    page?: number | undefined,
    pageSize?: number | undefined,
    includeReserved?: boolean | undefined,
    onlyArchived?: boolean | undefined,
    includeArchived?: boolean | undefined,
  ): Observable<RelationSearchResults> {
    let url_ = this.baseUrl + '/v2/customer-query/search-all-customers?';
    if (searchTerm === null) throw new globalThis.Error("The parameter 'searchTerm' cannot be null.");
    else if (searchTerm !== undefined) url_ += 'searchTerm=' + encodeURIComponent('' + searchTerm) + '&';
    if (orderBy === null) throw new globalThis.Error("The parameter 'orderBy' cannot be null.");
    else if (orderBy !== undefined) url_ += 'orderBy=' + encodeURIComponent('' + orderBy) + '&';
    if (orderByDescending === null) throw new globalThis.Error("The parameter 'orderByDescending' cannot be null.");
    else if (orderByDescending !== undefined) url_ += 'orderByDescending=' + encodeURIComponent('' + orderByDescending) + '&';
    if (page === null) throw new globalThis.Error("The parameter 'page' cannot be null.");
    else if (page !== undefined) url_ += 'page=' + encodeURIComponent('' + page) + '&';
    if (pageSize === null) throw new globalThis.Error("The parameter 'pageSize' cannot be null.");
    else if (pageSize !== undefined) url_ += 'pageSize=' + encodeURIComponent('' + pageSize) + '&';
    if (includeReserved === null) throw new globalThis.Error("The parameter 'includeReserved' cannot be null.");
    else if (includeReserved !== undefined) url_ += 'includeReserved=' + encodeURIComponent('' + includeReserved) + '&';
    if (onlyArchived === null) throw new globalThis.Error("The parameter 'onlyArchived' cannot be null.");
    else if (onlyArchived !== undefined) url_ += 'onlyArchived=' + encodeURIComponent('' + onlyArchived) + '&';
    if (includeArchived === null) throw new globalThis.Error("The parameter 'includeArchived' cannot be null.");
    else if (includeArchived !== undefined) url_ += 'includeArchived=' + encodeURIComponent('' + includeArchived) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSearchAllCustomers(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSearchAllCustomers(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<RelationSearchResults>;
            }
          } else return _observableThrow(response_) as any as Observable<RelationSearchResults>;
        }),
      );
  }

  protected processSearchAllCustomers(response: HttpResponseBase): Observable<RelationSearchResults> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = RelationSearchResults.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  getCustomer(id: string): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer-query/get-customer/{id}';
    if (id === undefined || id === null) throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace('{id}', encodeURIComponent('' + id));
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetCustomer(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetCustomer(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processGetCustomer(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomerResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return OK
   */
  getCustomers(id?: string[] | undefined): Observable<CustomersResponse> {
    let url_ = this.baseUrl + '/v2/customer-query/get-customers?';
    if (id === null) throw new globalThis.Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      id &&
      id.forEach((item) => {
        url_ += 'id=' + encodeURIComponent('' + item) + '&';
      });
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetCustomers(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetCustomers(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomersResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomersResponse>;
        }),
      );
  }

  protected processGetCustomers(response: HttpResponseBase): Observable<CustomersResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomersResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  hasCustomers(): Observable<HasItemsResult> {
    let url_ = this.baseUrl + '/v2/customer-query/has-customers';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processHasCustomers(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processHasCustomers(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<HasItemsResult>;
            }
          } else return _observableThrow(response_) as any as Observable<HasItemsResult>;
        }),
      );
  }

  protected processHasCustomers(response: HttpResponseBase): Observable<HasItemsResult> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = HasItemsResult.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  getNextCustomerCode(): Observable<number> {
    let url_ = this.baseUrl + '/v2/customer-query/get-next-customer-code';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetNextCustomerCode(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetNextCustomerCode(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<number>;
            }
          } else return _observableThrow(response_) as any as Observable<number>;
        }),
      );
  }

  protected processGetNextCustomerCode(response: HttpResponseBase): Observable<number> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : (null as any);

          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param name (optional)
   * @return OK
   */
  getFirstCustomerByName(name?: string | undefined): Observable<CustomerResponse> {
    let url_ = this.baseUrl + '/v2/customer-query/get-first-customer-by-name?';
    if (name === null) throw new globalThis.Error("The parameter 'name' cannot be null.");
    else if (name !== undefined) url_ += 'name=' + encodeURIComponent('' + name) + '&';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetFirstCustomerByName(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetFirstCustomerByName(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CustomerResponse>;
            }
          } else return _observableThrow(response_) as any as Observable<CustomerResponse>;
        }),
      );
  }

  protected processGetFirstCustomerByName(response: HttpResponseBase): Observable<CustomerResponse> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = CustomerResponse.fromJS(resultData200);
          return _observableOf(result200);
        }),
      );
    } else if (status === 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('No Content', status, _responseText, _headers);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @return OK
   */
  getAvailableCollectionTypes(): Observable<CollectionType[]> {
    let url_ = this.baseUrl + '/v2/customer-query/get-available-collection-types';
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAvailableCollectionTypes(response_);
        }),
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAvailableCollectionTypes(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<CollectionType[]>;
            }
          } else return _observableThrow(response_) as any as Observable<CollectionType[]>;
        }),
      );
  }

  protected processGetAvailableCollectionTypes(response: HttpResponseBase): Observable<CollectionType[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
          ? (response as any).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200) result200!.push(item);
          } else {
            result200 = null as any;
          }
          return _observableOf(result200);
        }),
      );
    } else if (status === 400) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result400: any = null;
          let resultData400 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result400 = ProblemDetails.fromJS(resultData400);
          return throwException('Bad Request', status, _responseText, _headers, result400);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result401: any = null;
          let resultData401 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result401 = ProblemDetails.fromJS(resultData401);
          return throwException('Unauthorized', status, _responseText, _headers, result401);
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result403: any = null;
          let resultData403 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result403 = ProblemDetails.fromJS(resultData403);
          return throwException('Forbidden', status, _responseText, _headers, result403);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result404: any = null;
          let resultData404 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
          result404 = ProblemDetails.fromJS(resultData404);
          return throwException('Not Found', status, _responseText, _headers, result404);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException('An unexpected server error occurred.', status, _responseText, _headers);
        }),
      );
    }
    return _observableOf(null as any);
  }
}

export class RelationSearchCriteria {
  searchTerm?: string;
  /** Page number, starting at 0 */
  page = 0;
  /** Page size, can be between 0 and 100.
   A size of 0 is useful when you only want to return the count. */
  pageSize = 24;
  sortByColumn?: RelationColumn;
  sortDirection?: SortDirection;
  /** Represents the translations for the enum values being sorted on. */
  sortEnumTranslations?: IEnumTranslation[];
  filters?: {
    alphaNumeric?: IRelationColumnAlphaNumericFilter[];
    numeric?: IRelationColumnNumericFilter[];
    date?: IRelationColumnDateFilter[];
  };

  public asSearchCriteria(): SearchCriteria {
    return {
      searchTerm: this.searchTerm,
      skip: this.page * this.pageSize,
      take: this.pageSize,
      sortByFieldName: this.sortByColumn ?? RelationColumn.Code,
      sortDescending: this.sortDirection === SortDirection.Descending,
      columnFilters: {
        alphaNumeric:
          this.filters?.alphaNumeric?.map((x) => ({
            columnName: x.columnName,
            value: x.value!,
          })) || [],
        numeric:
          this.filters?.numeric?.map((x) => ({
            columnName: x.columnName,
            type: x.type as unknown as ColumnNumericFilterType,
            value1: x.type === NumericFilterType.Between ? x.lowerBound! : x.value!,
            value2: x.type === NumericFilterType.Between ? x.upperBound : undefined,
          })) || [],
        date:
          this.filters?.date?.map((x) => ({
            columnName: x.columnName,
            value1: x.fromDate,
            value2: x.tillDate,
          })) || [],
      },
    };
  }

  private static getRelationColumn(fromColumn: string | undefined | null) {
    if (!fromColumn) return undefined;
    return Object.values(RelationColumn).find((column) => column.toLowerCase() === fromColumn.toLowerCase());
  }

  public static fromSearchCriteria(
    criteria: SearchCriteria,
    addIsActiveFilter = true,
    addNoReservedRelationsFilter = true,
  ): RelationSearchCriteria {
    const result = new RelationSearchCriteria();
    result.searchTerm = criteria.searchTerm;
    result.page = (criteria.skip || 0) / (criteria.take || 24);
    result.pageSize = criteria.take || 24;
    result.sortByColumn = this.getRelationColumn(criteria.sortByFieldName);
    result.sortDirection = criteria.sortDescending ? SortDirection.Descending : SortDirection.Ascending;
    result.sortEnumTranslations = undefined;
    result.filters = {
      alphaNumeric: criteria.columnFilters?.alphaNumeric
        ?.map<IRelationColumnAlphaNumericFilter>((x) => ({
          columnName: this.getRelationColumn(x.columnName)!,
          type: AlphaNumericFilterType.EqualTo,
          value: x.value,
        }))
        .filter((x) => x.columnName),
      numeric: criteria.columnFilters?.numeric
        ?.map<IRelationColumnNumericFilter>((x) => ({
          columnName: this.getRelationColumn(x.columnName)!,
          type: x.type as unknown as NumericFilterType,
          value: x.type !== ColumnNumericFilterType.Between ? x.value1 : undefined,
          lowerBound: x.type === ColumnNumericFilterType.Between ? x.value1 : undefined,
          upperBound: x.type === ColumnNumericFilterType.Between ? x.value2 : undefined,
        }))
        .filter((x) => x.columnName),
      date: criteria.columnFilters?.date
        ?.map<IRelationColumnDateFilter>((x) => ({
          columnName: this.getRelationColumn(x.columnName)!,
          fromDate: x.value1,
          tillDate: x.value2,
        }))
        .filter((x) => x.columnName),
    };

    const isActive = (criteria as Record<string, string | undefined>)['IsActive'];
    if (isActive !== undefined) {
      result.filters.alphaNumeric ??= [];
      result.filters.alphaNumeric.push({
        columnName: RelationColumn.IsActive,
        type: AlphaNumericFilterType.EqualTo,
        value: isActive,
      });
    }

    if (isActive === undefined && addIsActiveFilter) {
      result.filters.alphaNumeric ??= [];
      result.filters.alphaNumeric.push({
        columnName: RelationColumn.IsActive,
        type: AlphaNumericFilterType.EqualTo,
        value: 'True',
      });
    }

    if (addNoReservedRelationsFilter && !result.filters.numeric?.some((x) => x.columnName === RelationColumn.Code)) {
      result.filters.numeric ??= [];
      result.filters.numeric.push({
        columnName: RelationColumn.Code,
        type: NumericFilterType.GreaterThanOrEqualTo,
        value: 2,
      });
    }

    return result;
  }
}

export enum RelationType {
  Unspecified = 'Unspecified',
  Customer = 'Customer',
  Supplier = 'Supplier',
  AdministrationOwner = 'AdministrationOwner',
}

export class AdministrationsQueryRequest implements IAdministrationsQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;

  constructor(data?: IAdministrationsQueryRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.skip = _data['skip'];
      this.take = _data['take'];
      this.searchTerm = _data['searchTerm'];
      if (Array.isArray(_data['searchFields'])) {
        this.searchFields = [] as any;
        for (let item of _data['searchFields']) this.searchFields!.push(item);
      }
      if (_data['searchTermsByField']) {
        this.searchTermsByField = {} as any;
        for (let key in _data['searchTermsByField']) {
          if (_data['searchTermsByField'].hasOwnProperty(key))
            (<any>this.searchTermsByField)![key] =
              _data['searchTermsByField'][key] !== undefined ? _data['searchTermsByField'][key] : [];
        }
      }
      this.sortByFieldName = _data['sortByFieldName'];
      this.isSortByDescending = _data['isSortByDescending'];
    }
  }

  static fromJS(data: any): AdministrationsQueryRequest {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationsQueryRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['skip'] = this.skip;
    data['take'] = this.take;
    data['searchTerm'] = this.searchTerm;
    if (Array.isArray(this.searchFields)) {
      data['searchFields'] = [];
      for (let item of this.searchFields) data['searchFields'].push(item);
    }
    if (this.searchTermsByField) {
      data['searchTermsByField'] = {};
      for (let key in this.searchTermsByField) {
        if (this.searchTermsByField.hasOwnProperty(key))
          (<any>data['searchTermsByField'])[key] = (<any>this.searchTermsByField)[key];
      }
    }
    data['sortByFieldName'] = this.sortByFieldName;
    data['isSortByDescending'] = this.isSortByDescending;
    return data;
  }
}

export interface IAdministrationsQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;
}

export interface IOutdatedAdministrationsQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;
  includeExcludedAdministrations?: boolean;
}

export class AdministrationsQueryResponse implements IAdministrationsQueryResponse {
  items?: AdministrationResult[] | undefined;
  totalCount?: number;

  constructor(data?: IAdministrationsQueryResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(AdministrationResult.fromJS(item));
      }
      this.totalCount = _data['totalCount'];
    }
  }

  static fromJS(data: any): AdministrationsQueryResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationsQueryResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item.toJSON());
    }
    data['totalCount'] = this.totalCount;
    return data;
  }
}

export interface IAdministrationsQueryResponse {
  items?: AdministrationResult[] | undefined;
  totalCount?: number;
}

export class AdministrationResult implements IAdministrationResult {
  id?: string;
  name?: string | undefined;
  type?: number;
  status?: number;
  lastOpened?: Date | undefined;
  lastOpenedBy?: string | undefined;
  owner?: string | undefined;
  isDiva?: boolean;

  constructor(data?: IAdministrationResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
      this.name = _data['name'];
      this.type = _data['type'];
      this.status = _data['status'];
      this.lastOpened = _data['lastOpened'] ? new Date(_data['lastOpened'].toString()) : <any>undefined;
      this.lastOpenedBy = _data['lastOpenedBy'];
      this.owner = _data['owner'];
      this.isDiva = _data['isDiva'];
    }
  }

  static fromJS(data: any): AdministrationResult {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    data['name'] = this.name;
    data['type'] = this.type;
    data['status'] = this.status;
    data['lastOpened'] = this.lastOpened ? this.lastOpened.toISOString() : <any>undefined;
    data['lastOpenedBy'] = this.lastOpenedBy;
    data['owner'] = this.owner;
    data['isDiva'] = this.isDiva;
    return data;
  }
}

export interface IAdministrationResult {
  id?: string;
  name?: string | undefined;
  type?: number;
  status?: number;
  lastOpened?: Date | undefined;
  lastOpenedBy?: string | undefined;
  owner?: string | undefined;
  isDiva?: boolean;
}

export class AdministrationOwnersQueryRequest implements IAdministrationOwnersQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;

  constructor(data?: IAdministrationOwnersQueryRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.skip = _data['skip'];
      this.take = _data['take'];
      this.searchTerm = _data['searchTerm'];
      if (Array.isArray(_data['searchFields'])) {
        this.searchFields = [] as any;
        for (let item of _data['searchFields']) this.searchFields!.push(item);
      }
      if (_data['searchTermsByField']) {
        this.searchTermsByField = {} as any;
        for (let key in _data['searchTermsByField']) {
          if (_data['searchTermsByField'].hasOwnProperty(key))
            (<any>this.searchTermsByField)![key] =
              _data['searchTermsByField'][key] !== undefined ? _data['searchTermsByField'][key] : [];
        }
      }
      this.sortByFieldName = _data['sortByFieldName'];
      this.isSortByDescending = _data['isSortByDescending'];
    }
  }

  static fromJS(data: any): AdministrationOwnersQueryRequest {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationOwnersQueryRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['skip'] = this.skip;
    data['take'] = this.take;
    data['searchTerm'] = this.searchTerm;
    if (Array.isArray(this.searchFields)) {
      data['searchFields'] = [];
      for (let item of this.searchFields) data['searchFields'].push(item);
    }
    if (this.searchTermsByField) {
      data['searchTermsByField'] = {};
      for (let key in this.searchTermsByField) {
        if (this.searchTermsByField.hasOwnProperty(key))
          (<any>data['searchTermsByField'])[key] = (<any>this.searchTermsByField)[key];
      }
    }
    data['sortByFieldName'] = this.sortByFieldName;
    data['isSortByDescending'] = this.isSortByDescending;
    return data;
  }
}

export interface IAdministrationOwnersQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;
}

export class AdministrationNameResult implements IAdministrationNameResult {
  name?: string | undefined;

  constructor(data?: IAdministrationNameResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data['name'];
    }
  }

  static fromJS(data: any): AdministrationNameResult {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationNameResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['name'] = this.name;
    return data;
  }
}

export interface IAdministrationNameResult {
  name?: string | undefined;
}

export class AdministrationNamesQueryRequest implements IAdministrationNamesQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;

  constructor(data?: IAdministrationNamesQueryRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.skip = _data['skip'];
      this.take = _data['take'];
      this.searchTerm = _data['searchTerm'];
      if (Array.isArray(_data['searchFields'])) {
        this.searchFields = [] as any;
        for (let item of _data['searchFields']) this.searchFields!.push(item);
      }
      if (_data['searchTermsByField']) {
        this.searchTermsByField = {} as any;
        for (let key in _data['searchTermsByField']) {
          if (_data['searchTermsByField'].hasOwnProperty(key))
            (<any>this.searchTermsByField)![key] =
              _data['searchTermsByField'][key] !== undefined ? _data['searchTermsByField'][key] : [];
        }
      }
      this.sortByFieldName = _data['sortByFieldName'];
      this.isSortByDescending = _data['isSortByDescending'];
    }
  }

  static fromJS(data: any): AdministrationNamesQueryRequest {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationNamesQueryRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['skip'] = this.skip;
    data['take'] = this.take;
    data['searchTerm'] = this.searchTerm;
    if (Array.isArray(this.searchFields)) {
      data['searchFields'] = [];
      for (let item of this.searchFields) data['searchFields'].push(item);
    }
    if (this.searchTermsByField) {
      data['searchTermsByField'] = {};
      for (let key in this.searchTermsByField) {
        if (this.searchTermsByField.hasOwnProperty(key))
          (<any>data['searchTermsByField'])[key] = (<any>this.searchTermsByField)[key];
      }
    }
    data['sortByFieldName'] = this.sortByFieldName;
    data['isSortByDescending'] = this.isSortByDescending;
    return data;
  }
}

export interface IAdministrationNamesQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;
}

export class AdministrationNamesQueryResponse implements IAdministrationNamesQueryResponse {
  items?: AdministrationNameResult[] | undefined;
  totalCount?: number;

  constructor(data?: IAdministrationNamesQueryResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(AdministrationNameResult.fromJS(item));
      }
      this.totalCount = _data['totalCount'];
    }
  }

  static fromJS(data: any): AdministrationNamesQueryResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationNamesQueryResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item.toJSON());
    }
    data['totalCount'] = this.totalCount;
    return data;
  }
}

export interface IAdministrationNamesQueryResponse {
  items?: AdministrationNameResult[] | undefined;
  totalCount?: number;
}

export class AdministrationOwnersQueryResponse implements IAdministrationOwnersQueryResponse {
  items?: AdministrationOwnerResult[] | undefined;
  totalCount?: number;

  constructor(data?: IAdministrationOwnersQueryResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(AdministrationOwnerResult.fromJS(item));
      }
      this.totalCount = _data['totalCount'];
    }
  }

  static fromJS(data: any): AdministrationOwnersQueryResponse {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationOwnersQueryResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item.toJSON());
    }
    data['totalCount'] = this.totalCount;
    return data;
  }
}

export interface IAdministrationOwnersQueryResponse {
  items?: AdministrationOwnerResult[] | undefined;
  totalCount?: number;
}

export class AdministrationOwnerResult implements IAdministrationOwnerResult {
  owner?: string | undefined;

  constructor(data?: IAdministrationOwnerResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.owner = _data['owner'];
    }
  }

  static fromJS(data: any): AdministrationOwnerResult {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationOwnerResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['owner'] = this.owner;
    return data;
  }
}

export interface IAdministrationOwnerResult {
  owner?: string | undefined;
}

export class DomainProblemDetails implements IDomainProblemDetails {
  type?: string | undefined;
  title?: string | undefined;
  status?: number | undefined;
  detail?: string | undefined;
  instance?: string | undefined;
  errors?: DomainErrorModelErrors;

  [key: string]: any;

  constructor(data?: IDomainProblemDetails) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      for (var property in _data) {
        if (_data.hasOwnProperty(property)) this[property] = _data[property];
      }
      this.type = _data['type'];
      this.title = _data['title'];
      this.status = _data['status'];
      this.detail = _data['detail'];
      this.instance = _data['instance'];
      this.errors = _data['errors'] ? DomainErrorModelErrors.fromJS(_data['errors']) : <any>undefined;
    }
  }

  static fromJS(data: any): DomainProblemDetails {
    data = typeof data === 'object' ? data : {};
    let result = new DomainProblemDetails();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    for (var property in this) {
      if (this.hasOwnProperty(property)) data[property] = this[property];
    }
    data['type'] = this.type;
    data['title'] = this.title;
    data['status'] = this.status;
    data['detail'] = this.detail;
    data['instance'] = this.instance;
    data['errors'] = this.errors ? this.errors.toJSON() : <any>undefined;
    return data;
  }
}

export interface IDomainProblemDetails {
  type?: string | undefined;
  title?: string | undefined;
  status?: number | undefined;
  detail?: string | undefined;
  instance?: string | undefined;
  errors?: DomainErrorModelErrors;

  [key: string]: any;
}

class DomainErrorModelErrors implements IDomainErrorModelErrors {
  global?: DomainErrorModel[] | undefined;
  field?: { [key: string]: DomainErrorModel[] } | undefined;

  constructor(data?: IDomainErrorModelErrors) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['global'])) {
        this.global = [] as any;
        for (let item of _data['global']) this.global!.push(DomainErrorModel.fromJS(item));
      }
      if (_data['field']) {
        this.field = {} as any;
        for (let key in _data['field']) {
          if (_data['field'].hasOwnProperty(key))
            (<any>this.field)![key] = _data['field'][key]
              ? _data['field'][key].map((i: any) => DomainErrorModel.fromJS(i))
              : [];
        }
      }
    }
  }

  static fromJS(data: any): DomainErrorModelErrors {
    data = typeof data === 'object' ? data : {};
    let result = new DomainErrorModelErrors();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.global)) {
      data['global'] = [];
      for (let item of this.global) data['global'].push(item.toJSON());
    }
    if (this.field) {
      data['field'] = {};
      for (let key in this.field) {
        if (this.field.hasOwnProperty(key)) (<any>data['field'])[key] = (<any>this.field)[key];
      }
    }
    return data;
  }
}

export interface IDomainErrorModelErrors {
  global?: DomainErrorModel[] | undefined;
  field?: { [key: string]: DomainErrorModel[] } | undefined;
}

export class DomainErrorModel implements IDomainErrorModel {
  errorCode?: AdministrationManagementErrorCodes;
  readonly message?: string | undefined;
  readonly instanceId?: string | undefined;

  constructor(data?: IDomainErrorModel) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.errorCode = _data['errorCode'];
      (<any>this).message = _data['message'];
      (<any>this).instanceId = _data['instanceId'];
    }
  }

  static fromJS(data: any): DomainErrorModel {
    data = typeof data === 'object' ? data : {};
    let result = new DomainErrorModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['errorCode'] = this.errorCode;
    data['message'] = this.message;
    data['instanceId'] = this.instanceId;
    return data;
  }
}

export interface IDomainErrorModel {
  errorCode?: AdministrationManagementErrorCodes;
  message?: string | undefined;
  instanceId?: string | undefined;
}

export enum AdministrationManagementErrorCodes {
  AdministrationNotFound = 'AdministrationNotFound',
  DataEmpty = 'DataEmpty',
  DataNotUnique = 'DataNotUnique',
  FieldNotFound = 'FieldNotFound',
  ForbiddenOperation = 'ForbiddenOperation',
  NoAccess = 'NoAccess',
  ProgrammaticError = 'ProgrammaticError',
  NullOrEmpty = 'NullOrEmpty',
  IncorrectTypeOrFormat = 'IncorrectTypeOrFormat',
  BelowMinimum = 'BelowMinimum',
  AboveMaximum = 'AboveMaximum',
  ConstraintViolation = 'ConstraintViolation',
  ConflictingValue = 'ConflictingValue',
}

export class PermissionStateModel implements IPermissionStateModel {
  permission?: PermissionType;
  readonly hasPermission?: boolean;

  constructor(data?: IPermissionStateModel) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.permission = _data['permission'];
      (<any>this).hasPermission = _data['hasPermission'];
    }
  }

  static fromJS(data: any): PermissionStateModel {
    data = typeof data === 'object' ? data : {};
    let result = new PermissionStateModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['permission'] = this.permission;
    data['hasPermission'] = this.hasPermission;
    return data;
  }
}

export interface IPermissionStateModel {
  permission?: PermissionType;
  hasPermission?: boolean;
}

export enum PermissionType {
  CreateNewAdministration = 'CreateNewAdministration',
  CopyAndBackup = 'CopyAndBackup',
  ManageOwnership = 'ManageOwnership',
  RemoveAndRenameAdministration = 'RemoveAndRenameAdministration',
}

export class ShelvedAdministrationResult implements IShelvedAdministrationResult {
  identifier?: string;
  name?: string | undefined;
  databaseName?: string | undefined;
  status?: AdministrationStatus;
  createdOn?: Date;
  serverName?: string | undefined;
  serverPoolName?: string | undefined;

  constructor(data?: IShelvedAdministrationResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.identifier = _data['identifier'];
      this.name = _data['name'];
      this.databaseName = _data['databaseName'];
      this.status = _data['status'];
      this.createdOn = _data['createdOn'] ? new Date(_data['createdOn'].toString()) : <any>undefined;
      this.serverName = _data['serverName'];
      this.serverPoolName = _data['serverPoolName'];
    }
  }

  static fromJS(data: any): ShelvedAdministrationResult {
    data = typeof data === 'object' ? data : {};
    let result = new ShelvedAdministrationResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['identifier'] = this.identifier;
    data['name'] = this.name;
    data['databaseName'] = this.databaseName;
    data['status'] = this.status;
    data['createdOn'] = this.createdOn ? this.createdOn.toISOString() : <any>undefined;
    data['serverName'] = this.serverName;
    data['serverPoolName'] = this.serverPoolName;
    return data;
  }
}

export interface IShelvedAdministrationResult {
  identifier?: string;
  name?: string | undefined;
  databaseName?: string | undefined;
  status?: AdministrationStatus;
  createdOn?: Date;
  serverName?: string | undefined;
  serverPoolName?: string | undefined;
}

export class OutdatedAdministrationResult implements IOutdatedAdministrationResult {
  identifier?: string;
  name?: string | undefined;
  accountId?: number | undefined;
  owner?: string | undefined;
  status?: AdministrationStatus;
  createdOn?: Date;
  lastOpened?: Date | undefined;
  serverName?: string | undefined;
  serverPoolName?: string | undefined;
  excludedFromDeletion?: boolean;
  excludedFromDeletionBasedOnAccountId?: boolean;

  constructor(data?: IOutdatedAdministrationResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.identifier = _data['identifier'];
      this.name = _data['name'];
      this.accountId = _data['accountId'];
      this.owner = _data['owner'];
      this.status = _data['status'];
      this.createdOn = _data['createdOn'] ? new Date(_data['createdOn'].toString()) : <any>undefined;
      this.lastOpened = _data['lastOpened'] ? new Date(_data['lastOpened'].toString()) : <any>undefined;
      this.serverName = _data['serverName'];
      this.serverPoolName = _data['serverPoolName'];
      this.excludedFromDeletion = _data['excludedFromDeletion'];
      this.excludedFromDeletionBasedOnAccountId = _data['excludedFromDeletionBasedOnAccountId'];
    }
  }

  static fromJS(data: any): OutdatedAdministrationResult {
    data = typeof data === 'object' ? data : {};
    let result = new OutdatedAdministrationResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['identifier'] = this.identifier;
    data['name'] = this.name;
    data['accountId'] = this.accountId;
    data['owner'] = this.owner;
    data['status'] = this.status;
    data['createdOn'] = this.createdOn ? this.createdOn.toISOString() : <any>undefined;
    data['lastOpened'] = this.lastOpened ? this.lastOpened.toISOString() : <any>undefined;
    data['serverName'] = this.serverName;
    data['serverPoolName'] = this.serverPoolName;
    data['excludedFromDeletion'] = this.excludedFromDeletion;
    data['excludedFromDeletionBasedOnAccountId'] = this.excludedFromDeletionBasedOnAccountId;
    return data;
  }
}

export interface IOutdatedAdministrationResult {
  identifier?: string;
  name?: string | undefined;
  accountId?: number | undefined;
  owner?: string | undefined;
  status?: AdministrationStatus;
  createdOn?: Date;
  lastOpened?: Date | undefined;
  serverName?: string | undefined;
  serverPoolName?: string | undefined;
  excludedFromDeletion?: boolean;
  excludedFromDeletionBasedOnAccountId?: boolean;
}

export class OutdatedAdministrationsQueryRequest implements IOutdatedAdministrationsQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;
  includeExcludedAdministrations?: boolean;

  constructor(data?: IOutdatedAdministrationsQueryRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.skip = _data['skip'];
      this.take = _data['take'];
      this.searchTerm = _data['searchTerm'];
      if (Array.isArray(_data['searchFields'])) {
        this.searchFields = [] as any;
        for (let item of _data['searchFields']) this.searchFields!.push(item);
      }
      if (_data['searchTermsByField']) {
        this.searchTermsByField = {} as any;
        for (let key in _data['searchTermsByField']) {
          if (_data['searchTermsByField'].hasOwnProperty(key))
            (<any>this.searchTermsByField)![key] =
              _data['searchTermsByField'][key] !== undefined ? _data['searchTermsByField'][key] : [];
        }
      }
      this.sortByFieldName = _data['sortByFieldName'];
      this.isSortByDescending = _data['isSortByDescending'];
      this.includeExcludedAdministrations = _data['includeExcludedAdministrations'];
    }
  }

  static fromJS(data: any): OutdatedAdministrationsQueryRequest {
    data = typeof data === 'object' ? data : {};
    let result = new OutdatedAdministrationsQueryRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['skip'] = this.skip;
    data['take'] = this.take;
    data['searchTerm'] = this.searchTerm;
    if (Array.isArray(this.searchFields)) {
      data['searchFields'] = [];
      for (let item of this.searchFields) data['searchFields'].push(item);
    }
    if (this.searchTermsByField) {
      data['searchTermsByField'] = {};
      for (let key in this.searchTermsByField) {
        if (this.searchTermsByField.hasOwnProperty(key))
          (<any>data['searchTermsByField'])[key] = (<any>this.searchTermsByField)[key];
      }
    }
    data['sortByFieldName'] = this.sortByFieldName;
    data['isSortByDescending'] = this.isSortByDescending;
    data['includeExcludedAdministrations'] = this.includeExcludedAdministrations;
    return data;
  }
}

export interface IOutdatedAdministrationsQueryRequest {
  skip?: number;
  take?: number;
  searchTerm?: string | undefined;
  searchFields?: string[] | undefined;
  searchTermsByField?: { [key: string]: string[] } | undefined;
  sortByFieldName?: string | undefined;
  isSortByDescending?: boolean;
  includeExcludedAdministrations?: boolean;
}

export class OutdatedAdministrationsQueryResponse implements IOutdatedAdministrationsQueryResponse {
  items?: OutdatedAdministrationResult[] | undefined;
  totalCount?: number;

  constructor(data?: IOutdatedAdministrationsQueryResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['items'])) {
        this.items = [] as any;
        for (let item of _data['items']) this.items!.push(OutdatedAdministrationResult.fromJS(item));
      }
      this.totalCount = _data['totalCount'];
    }
  }

  static fromJS(data: any): OutdatedAdministrationsQueryResponse {
    data = typeof data === 'object' ? data : {};
    let result = new OutdatedAdministrationsQueryResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.items)) {
      data['items'] = [];
      for (let item of this.items) data['items'].push(item.toJSON());
    }
    data['totalCount'] = this.totalCount;
    return data;
  }
}

export interface IOutdatedAdministrationsQueryResponse {
  items?: OutdatedAdministrationResult[] | undefined;
  totalCount?: number;
}

export class GhostAdministrationResult implements IGhostAdministrationResult {
  identifier?: string;
  name?: string | undefined;
  accountId?: number | undefined;
  status?: AdministrationStatus;
  createdOn?: Date;
  lastOpened?: Date | undefined;
  lastOpenedBy?: string | undefined;

  constructor(data?: IGhostAdministrationResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.identifier = _data['identifier'];
      this.name = _data['name'];
      this.accountId = _data['accountId'];
      this.status = _data['status'];
      this.createdOn = _data['createdOn'] ? new Date(_data['createdOn'].toString()) : <any>undefined;
      this.lastOpened = _data['lastOpened'] ? new Date(_data['lastOpened'].toString()) : <any>undefined;
      this.lastOpenedBy = _data['lastOpenedBy'];
    }
  }

  static fromJS(data: any): GhostAdministrationResult {
    data = typeof data === 'object' ? data : {};
    let result = new GhostAdministrationResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['identifier'] = this.identifier;
    data['name'] = this.name;
    data['accountId'] = this.accountId;
    data['status'] = this.status;
    data['createdOn'] = this.createdOn ? this.createdOn.toISOString() : <any>undefined;
    data['lastOpened'] = this.lastOpened ? this.lastOpened.toISOString() : <any>undefined;
    data['lastOpenedBy'] = this.lastOpenedBy;
    return data;
  }
}

export interface IGhostAdministrationResult {
  identifier?: string;
  name?: string | undefined;
  accountId?: number | undefined;
  status?: AdministrationStatus;
  createdOn?: Date;
  lastOpened?: Date | undefined;
  lastOpenedBy?: string | undefined;
}

export class AdministrationResultWithAvailableModuleActionsOld implements IAdministrationResultWithAvailableModuleActionsOld {
  id?: string;
  name?: string | undefined;
  type?: number;
  status?: number;
  lastOpened?: Date | undefined;
  lastOpenedBy?: string | undefined;
  owner?: string | undefined;
  isDiva?: boolean;
  availableModuleActions?: AdminstrationManagementModuleActions[] | undefined;

  constructor(data?: IAdministrationResultWithAvailableModuleActionsOld) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
      this.name = _data['name'];
      this.type = _data['type'];
      this.status = _data['status'];
      this.lastOpened = _data['lastOpened'] ? new Date(_data['lastOpened'].toString()) : <any>undefined;
      this.lastOpenedBy = _data['lastOpenedBy'];
      this.owner = _data['owner'];
      this.isDiva = _data['isDiva'];
      if (Array.isArray(_data['availableModuleActions'])) {
        this.availableModuleActions = [] as any;
        for (let item of _data['availableModuleActions']) this.availableModuleActions!.push(item);
      }
    }
  }

  static fromJS(data: any): AdministrationResultWithAvailableModuleActionsOld {
    data = typeof data === 'object' ? data : {};
    let result = new AdministrationResultWithAvailableModuleActionsOld();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    data['name'] = this.name;
    data['type'] = this.type;
    data['status'] = this.status;
    data['lastOpened'] = this.lastOpened ? this.lastOpened.toISOString() : <any>undefined;
    data['lastOpenedBy'] = this.lastOpenedBy;
    data['owner'] = this.owner;
    data['isDiva'] = this.isDiva;
    if (Array.isArray(this.availableModuleActions)) {
      data['availableModuleActions'] = [];
      for (let item of this.availableModuleActions) data['availableModuleActions'].push(item);
    }
    return data;
  }
}

export interface IAdministrationResultWithAvailableModuleActionsOld {
  id?: string;
  name?: string | undefined;
  type?: number;
  status?: number;
  lastOpened?: Date | undefined;
  lastOpenedBy?: string | undefined;
  owner?: string | undefined;
  isDiva?: boolean;
  availableModuleActions?: AdminstrationManagementModuleActions[] | undefined;
}

export class ZombieAdministrationResult implements IZombieAdministrationResult {
  databaseName?: string | undefined;
  sqlServerName?: string | undefined;
  elasticPoolName?: string | undefined;
  creationDate?: Date | undefined;
  status?: string | undefined;
  administrationStatus?: AdministrationStatus;

  constructor(data?: IZombieAdministrationResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.databaseName = _data['databaseName'];
      this.sqlServerName = _data['sqlServerName'];
      this.elasticPoolName = _data['elasticPoolName'];
      this.creationDate = _data['creationDate'] ? new Date(_data['creationDate'].toString()) : <any>undefined;
      this.status = _data['status'];
      this.administrationStatus = _data['administrationStatus'];
    }
  }

  static fromJS(data: any): ZombieAdministrationResult {
    data = typeof data === 'object' ? data : {};
    let result = new ZombieAdministrationResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['databaseName'] = this.databaseName;
    data['sqlServerName'] = this.sqlServerName;
    data['elasticPoolName'] = this.elasticPoolName;
    data['creationDate'] = this.creationDate ? this.creationDate.toISOString() : <any>undefined;
    data['status'] = this.status;
    data['administrationStatus'] = this.administrationStatus;
    return data;
  }
}

export interface IZombieAdministrationResult {
  databaseName?: string | undefined;
  sqlServerName?: string | undefined;
  elasticPoolName?: string | undefined;
  creationDate?: Date | undefined;
  status?: string | undefined;
  administrationStatus?: AdministrationStatus;
}

export enum AdminstrationManagementModuleActions {
  ViewErrorAlert = 'ViewErrorAlert',
  ViewWarningAlert = 'ViewWarningAlert',
  ViewAccountancyDashboardAdvancedView = 'ViewAccountancyDashboardAdvancedView',
  AdministrationAccess = 'AdministrationAccess',
  ViewAccountancyDashboardSales = 'ViewAccountancyDashboardSales',
  CopyAdministration = 'CopyAdministration',
  PointInTimeRestore = 'PointInTimeRestore',
  TransferAdministration = 'TransferAdministration',
  ViewBackups = 'ViewBackups',
  RemoveBackup = 'RemoveBackup',
  DownloadBackup = 'DownloadBackup',
  UploadBackup = 'UploadBackup',
  CreateBackup = 'CreateBackup',
  RemoveAdministration = 'RemoveAdministration',
  RestoreBackup = 'RestoreBackup',
  SearchWithPagination = 'SearchWithPagination',
  CreateAdministration = 'CreateAdministration',
  UseGeneralModuleActionsCreateAdministration = 'UseGeneralModuleActionsCreateAdministration',
  SearchOnAdministrationName = 'SearchOnAdministrationName',
  CreateNewAdministrationFLow = 'CreateNewAdministrationFLow',
  SearchOnAdministrationOwner = 'SearchOnAdministrationOwner',
  UseSearchAdministrationSelectionInOffer = 'UseSearchAdministrationSelectionInOffer',
  UseOrderingFilteringOnAccountancyDashboard = 'UseOrderingFilteringOnAccountancyDashboard',
}

export class ZombieServerPoolResult implements IZombieServerPoolResult {
  name?: string | undefined;
  creationDate?: Date | undefined;
  state?: string | undefined;
  edition?: string | undefined;

  constructor(data?: IZombieServerPoolResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data['name'];
      this.creationDate = _data['creationDate'] ? new Date(_data['creationDate'].toString()) : <any>undefined;
      this.state = _data['state'];
      this.edition = _data['edition'];
    }
  }

  static fromJS(data: any): ZombieServerPoolResult {
    data = typeof data === 'object' ? data : {};
    let result = new ZombieServerPoolResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['name'] = this.name;
    data['creationDate'] = this.creationDate ? this.creationDate.toISOString() : <any>undefined;
    data['state'] = this.state;
    data['edition'] = this.edition;
    return data;
  }
}

export interface IZombieServerPoolResult {
  name?: string | undefined;
  creationDate?: Date | undefined;
  state?: string | undefined;
  edition?: string | undefined;
}

export enum AdministrationStatus {
  Invalid = 'Invalid',
  Pending = 'Pending',
  Creating = 'Creating',
  Ready = 'Ready',
  MakingBackup = 'MakingBackup',
  RestoringBackup = 'RestoringBackup',
  PendingRemove = 'PendingRemove',
  Removed = 'Removed',
  PendingBackup = 'PendingBackup',
  NotReady = 'NotReady',
  Copying = 'Copying',
  Maintenance = 'Maintenance',
  RemovingFailed = 'RemovingFailed',
}

export interface GrootboekIdentifierResponse {
  result: GrootboekIdentifier[];
  totalCount: number;
}

export interface GrootboekIdentifier {
  id: number;
  publicIdentifier: string;
  number: number;
  description: string;
  functie: string;
  functieId: number;
  kostenplaatsInstelling: string;
  vatRateCode: string;
  btwOptie: string;
  isInkoop: boolean;
  isVerkoop: boolean;
  rekeningCode: string;
  rubriekId: number;
  isNonActive: boolean;
}

export class DistinctValuesResponse implements IDistinctValuesResponse {
  distinctValues!: string[];

  constructor(data?: IDistinctValuesResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
      }
    }
    if (!data) {
      this.distinctValues = [];
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['distinctValues'])) {
        this.distinctValues = [] as any;
        for (let item of _data['distinctValues']) this.distinctValues!.push(item);
      }
    }
  }

  static fromJS(data: any): DistinctValuesResponse {
    data = typeof data === 'object' ? data : {};
    let result = new DistinctValuesResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.distinctValues)) {
      data['distinctValues'] = [];
      for (let item of this.distinctValues) data['distinctValues'].push(item);
    }
    return data;
  }
}

export interface IDistinctValuesResponse {
  distinctValues: string[];
}

export enum RelationColumn {
  Id = 'Id',
  IsActive = 'IsActive',
  RelationType = 'RelationType',
  Code = 'Code',
  Name = 'Name',
  Memo = 'Memo',
  ChamberOfCommerceNumber = 'ChamberOfCommerceNumber',
  LegalForm = 'LegalForm',
  ValueAddedTaxNumber = 'ValueAddedTaxNumber',
  OrganizationIdentificationNumber = 'OrganizationIdentificationNumber',
  FiscalIdentifierType = 'FiscalIdentifierType',
  FiscalIdentifier = 'FiscalIdentifier',
  ContactPersonName = 'ContactPersonName',
  Salutation = 'Salutation',
  AddressAddressLine = 'AddressAddressLine',
  AddressPostalCode = 'AddressPostalCode',
  AddressCity = 'AddressCity',
  AddressCountry = 'AddressCountry',
  CorrespondanceContactPersonName = 'CorrespondanceContactPersonName',
  CorrespondanceAddressAddressLine = 'CorrespondanceAddressAddressLine',
  CorrespondanceAddressPostalCode = 'CorrespondanceAddressPostalCode',
  CorrespondanceAddressCity = 'CorrespondanceAddressCity',
  CorrespondanceAddressCountry = 'CorrespondanceAddressCountry',
  CorrespondanceRelation = 'CorrespondanceRelation',
  PhoneNumber = 'PhoneNumber',
  MobilePhoneNumber = 'MobilePhoneNumber',
  FaxNumber = 'FaxNumber',
  Email = 'Email',
  Website = 'Website',
  SaleQuotationEmailSettingIsActive = 'SaleQuotationEmailSettingIsActive',
  SaleQuotationEmailSettingTo = 'SaleQuotationEmailSettingTo',
  SaleQuotationEmailSettingCc = 'SaleQuotationEmailSettingCc',
  SaleConfirmationEmailSettingIsActive = 'SaleConfirmationEmailSettingIsActive',
  SaleConfirmationEmailSettingTo = 'SaleConfirmationEmailSettingTo',
  SaleConfirmationEmailSettingCc = 'SaleConfirmationEmailSettingCc',
  SaleInvoicingEmailSettingIsActive = 'SaleInvoicingEmailSettingIsActive',
  SaleInvoicingEmailSettingTo = 'SaleInvoicingEmailSettingTo',
  SaleInvoicingEmailSettingCc = 'SaleInvoicingEmailSettingCc',
  SaleInvoicingEmailSettingAttachUbl = 'SaleInvoicingEmailSettingAttachUbl',
  SalePaymentReminderEmailSettingIsActive = 'SalePaymentReminderEmailSettingIsActive',
  SalePaymentReminderEmailSettingTo = 'SalePaymentReminderEmailSettingTo',
  SalePaymentReminderEmailSettingCc = 'SalePaymentReminderEmailSettingCc',
  PurchaseQuotationEmailSettingIsActive = 'PurchaseQuotationEmailSettingIsActive',
  PurchaseQuotationEmailSettingTo = 'PurchaseQuotationEmailSettingTo',
  PurchaseQuotationEmailSettingCc = 'PurchaseQuotationEmailSettingCc',
  PurchaseOrderEmailSettingIsActive = 'PurchaseOrderEmailSettingIsActive',
  PurchaseOrderEmailSettingTo = 'PurchaseOrderEmailSettingTo',
  PurchaseOrderEmailSettingCc = 'PurchaseOrderEmailSettingCc',
  BankAccountNumber = 'BankAccountNumber',
  AccountHolderName = 'AccountHolderName',
  AccountHolderCity = 'AccountHolderCity',
  Iban = 'Iban',
  Bic = 'Bic',
  CreditCardNumber = 'CreditCardNumber',
  InvoiceDiscount = 'InvoiceDiscount',
  PaymentTermInDays = 'PaymentTermInDays',
  CreditLimit = 'CreditLimit',
  PaymentReminderType = 'PaymentReminderType',
  PaymentCollectionType = 'PaymentCollectionType',
  IncludeInvoicesInPaymentOrderSet = 'IncludeInvoicesInPaymentOrderSet',
  DebitNumber = 'DebitNumber',
  MinimumOrderAmount = 'MinimumOrderAmount',
  CustomerDiscountGroup = 'CustomerDiscountGroup',
  ModifiedOn = 'ModifiedOn',
  NumberOfFiscalAuthorizations = 'NumberOfFiscalAuthorizations',
  DateOfBirth = 'DateOfBirth',
  MainActivityCode = 'MainActivityCode',
  DefermentArrangementForFilingTaxReturn = 'DefermentArrangementForFilingTaxReturn',
}

export enum SortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export class ProblemDetails implements IProblemDetails {
  type?: string | undefined;
  title?: string | undefined;
  status?: number | undefined;
  detail?: string | undefined;
  instance?: string | undefined;

  [key: string]: any;

  constructor(data?: IProblemDetails) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      for (const property in _data) {
        if (_data.hasOwnProperty(property)) this[property] = _data[property];
      }
      this.type = _data['type'];
      this.title = _data['title'];
      this.status = _data['status'];
      this.detail = _data['detail'];
      this.instance = _data['instance'];
    }
  }

  static fromJS(data: any): ProblemDetails {
    data = typeof data === 'object' ? data : {};
    const result = new ProblemDetails();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    for (const property in this) {
      if (this.hasOwnProperty(property)) data[property] = this[property];
    }
    data['type'] = this.type;
    data['title'] = this.title;
    data['status'] = this.status;
    data['detail'] = this.detail;
    data['instance'] = this.instance;
    return data;
  }
}

export interface IProblemDetails {
  type?: string | undefined;
  title?: string | undefined;
  status?: number | undefined;
  detail?: string | undefined;
  instance?: string | undefined;

  [key: string]: any;
}

export interface IRelationColumnPaginationRequest {
  searchTerm?: string | undefined;
  /** Page number, starting at 0 */
  page: number;
  /** Page size, can be between 0 and 100.
   A size of 0 is useful when you only want to return the count. */
  pageSize: number;
  sortByColumn?: RelationColumn;
  sortDirection?: SortDirection;
  /** Represents the translations for the enum values being sorted on. */
  sortEnumTranslations?: EnumTranslation[] | undefined;
  filters?: RelationColumnColumnFilters;
}

export interface IEnumTranslation {
  key: string;
  value: string;
}

export class EnumTranslation implements IEnumTranslation {
  key!: string;
  value!: string;

  constructor(data?: IEnumTranslation) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.key = _data['key'];
      this.value = _data['value'];
    }
  }

  static fromJS(data: any): EnumTranslation {
    data = typeof data === 'object' ? data : {};
    const result = new EnumTranslation();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['key'] = this.key;
    data['value'] = this.value;
    return data;
  }
}

export interface IRelationColumnColumnFilters {
  alphaNumeric?: RelationColumnAlphaNumericFilter[] | undefined;
  numeric?: RelationColumnNumericFilter[] | undefined;
  date?: RelationColumnDateFilter[] | undefined;
}

export class RelationColumnDateFilter implements IRelationColumnDateFilter {
  columnName!: RelationColumn;
  fromDate?: Date | undefined;
  tillDate?: Date | undefined;

  constructor(data?: IRelationColumnDateFilter) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.columnName = _data['columnName'];
      this.fromDate = _data['fromDate'] ? new Date(_data['fromDate'].toString()) : (undefined as any);
      this.tillDate = _data['tillDate'] ? new Date(_data['tillDate'].toString()) : (undefined as any);
    }
  }

  static fromJS(data: any): RelationColumnDateFilter {
    data = typeof data === 'object' ? data : {};
    const result = new RelationColumnDateFilter();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['columnName'] = this.columnName;
    data['fromDate'] = this.fromDate ? formatDate(this.fromDate) : (undefined as any);
    data['tillDate'] = this.tillDate ? formatDate(this.tillDate) : (undefined as any);
    return data;
  }
}

export interface IBinaryData {
}

export enum CollectionType {
  Unknown = 'Unknown',
  None = 'None',
  Core = 'Core',
  B2B = 'B2B',
}

export class CustomerGroupObject implements ICustomerGroupObject {
  customerGroupIdentifier?: string;
  customerGroupNumber?: number | undefined;
  customerGroupDescription?: string | undefined;

  constructor(data?: ICustomerGroupObject) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.customerGroupIdentifier = _data['customerGroupIdentifier'];
      this.customerGroupNumber = _data['customerGroupNumber'];
      this.customerGroupDescription = _data['customerGroupDescription'];
    }
  }

  static fromJS(data: any): CustomerGroupObject {
    data = typeof data === 'object' ? data : {};
    const result = new CustomerGroupObject();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['customerGroupIdentifier'] = this.customerGroupIdentifier;
    data['customerGroupNumber'] = this.customerGroupNumber;
    data['customerGroupDescription'] = this.customerGroupDescription;
    return data;
  }
}

export interface ICustomerGroupObject {
  customerGroupIdentifier?: string;
  customerGroupNumber?: number | undefined;
  customerGroupDescription?: string | undefined;
}

export class CustomerRequest implements ICustomerRequest {
  id?: string | undefined;
  code?: number | undefined;
  isActive?: boolean | undefined;
  name!: string;
  legalForm?: LegalFormType;
  memo?: string | undefined;
  contactName?: string | undefined;
  salutation?: SalutationType;
  address?: AddressModel;
  correspondenceContactName?: string | undefined;
  correspondenceAddress?: AddressModel;
  phoneNumber?: string | undefined;
  mobileNumber?: string | undefined;
  faxNumber?: string | undefined;
  emailAddressLine?: string[] | undefined;
  website?: string | undefined;
  kvk?: string | undefined;
  vat?: string | undefined;
  oin?: string | undefined;
  fiscalIdentifierType?: FiscalIdentifierType;
  fiscalIdentifier?: string | undefined;
  numberOfFiscalAuthorizations?: number | undefined;
  dateOfBirth?: Date | undefined;
  mainActivityCode?: string | undefined;
  defermentArrangementForFilingTaxReturn?: boolean | undefined;
  paymentPreference?: PaymentPreferenceModel;
  invoiceRelationId?: string | undefined;
  customerGroupId?: string | undefined;
  customerGroup?: CustomerGroupObject;
  salesInvoicePreference?: SalesInvoicePreferenceModel;
  invoiceEmailPreference?: InvoiceEmailPreferenceModel;
  quotationEmailPreference?: EmailPreferenceModel;
  confirmationEmailPreference?: EmailPreferenceModel;
  exhortationEmailPreference?: EmailPreferenceModel;

  constructor(data?: ICustomerRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
      this.code = _data['code'];
      this.isActive = _data['isActive'];
      this.name = _data['name'];
      this.legalForm = _data['legalForm'];
      this.memo = _data['memo'];
      this.contactName = _data['contactName'];
      this.salutation = _data['salutation'];
      this.address = _data['address'] ? AddressModel.fromJS(_data['address']) : (undefined as any);
      this.correspondenceContactName = _data['correspondenceContactName'];
      this.correspondenceAddress = _data['correspondenceAddress']
        ? AddressModel.fromJS(_data['correspondenceAddress'])
        : (undefined as any);
      this.phoneNumber = _data['phoneNumber'];
      this.mobileNumber = _data['mobileNumber'];
      this.faxNumber = _data['faxNumber'];
      if (Array.isArray(_data['emailAddressLine'])) {
        this.emailAddressLine = [] as any;
        for (let item of _data['emailAddressLine']) this.emailAddressLine!.push(item);
      }
      this.website = _data['website'];
      this.kvk = _data['kvk'];
      this.vat = _data['vat'];
      this.oin = _data['oin'];
      this.fiscalIdentifierType = _data['fiscalIdentifierType'];
      this.fiscalIdentifier = _data['fiscalIdentifier'];
      this.numberOfFiscalAuthorizations = _data['numberOfFiscalAuthorizations'];
      this.dateOfBirth = _data['dateOfBirth'] ? new Date(_data['dateOfBirth'].toString()) : (undefined as any);
      this.mainActivityCode = _data['mainActivityCode'];
      this.defermentArrangementForFilingTaxReturn = _data['defermentArrangementForFilingTaxReturn'];
      this.paymentPreference = _data['paymentPreference']
        ? PaymentPreferenceModel.fromJS(_data['paymentPreference'])
        : (undefined as any);
      this.invoiceRelationId = _data['invoiceRelationId'];
      this.customerGroupId = _data['customerGroupId'];
      this.customerGroup = _data['customerGroup'] ? CustomerGroupObject.fromJS(_data['customerGroup']) : (undefined as any);
      this.salesInvoicePreference = _data['salesInvoicePreference']
        ? SalesInvoicePreferenceModel.fromJS(_data['salesInvoicePreference'])
        : (undefined as any);
      this.invoiceEmailPreference = _data['invoiceEmailPreference']
        ? InvoiceEmailPreferenceModel.fromJS(_data['invoiceEmailPreference'])
        : (undefined as any);
      this.quotationEmailPreference = _data['quotationEmailPreference']
        ? EmailPreferenceModel.fromJS(_data['quotationEmailPreference'])
        : (undefined as any);
      this.confirmationEmailPreference = _data['confirmationEmailPreference']
        ? EmailPreferenceModel.fromJS(_data['confirmationEmailPreference'])
        : (undefined as any);
      this.exhortationEmailPreference = _data['exhortationEmailPreference']
        ? EmailPreferenceModel.fromJS(_data['exhortationEmailPreference'])
        : (undefined as any);
    }
  }

  static fromJS(data: any): CustomerRequest {
    data = typeof data === 'object' ? data : {};
    let result = new CustomerRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    data['code'] = this.code;
    data['isActive'] = this.isActive;
    data['name'] = this.name;
    data['legalForm'] = this.legalForm;
    data['memo'] = this.memo;
    data['contactName'] = this.contactName;
    data['salutation'] = this.salutation;
    data['address'] = this.address ? this.address.toJSON() : (undefined as any);
    data['correspondenceContactName'] = this.correspondenceContactName;
    data['correspondenceAddress'] = this.correspondenceAddress ? this.correspondenceAddress.toJSON() : (undefined as any);
    data['phoneNumber'] = this.phoneNumber;
    data['mobileNumber'] = this.mobileNumber;
    data['faxNumber'] = this.faxNumber;
    if (Array.isArray(this.emailAddressLine)) {
      data['emailAddressLine'] = [];
      for (let item of this.emailAddressLine) data['emailAddressLine'].push(item);
    }
    data['website'] = this.website;
    data['kvk'] = this.kvk;
    data['vat'] = this.vat;
    data['oin'] = this.oin;
    data['fiscalIdentifierType'] = this.fiscalIdentifierType;
    data['fiscalIdentifier'] = this.fiscalIdentifier;
    data['numberOfFiscalAuthorizations'] = this.numberOfFiscalAuthorizations;
    data['dateOfBirth'] = this.dateOfBirth ? formatDate(this.dateOfBirth) : (undefined as any);
    data['mainActivityCode'] = this.mainActivityCode;
    data['defermentArrangementForFilingTaxReturn'] = this.defermentArrangementForFilingTaxReturn;
    data['paymentPreference'] = this.paymentPreference ? this.paymentPreference.toJSON() : (undefined as any);
    data['invoiceRelationId'] = this.invoiceRelationId;
    data['customerGroupId'] = this.customerGroupId;
    data['customerGroup'] = this.customerGroup ? this.customerGroup.toJSON() : (undefined as any);
    data['salesInvoicePreference'] = this.salesInvoicePreference ? this.salesInvoicePreference.toJSON() : (undefined as any);
    data['invoiceEmailPreference'] = this.invoiceEmailPreference ? this.invoiceEmailPreference.toJSON() : (undefined as any);
    data['quotationEmailPreference'] = this.quotationEmailPreference ? this.quotationEmailPreference.toJSON() : (undefined as any);
    data['confirmationEmailPreference'] = this.confirmationEmailPreference
      ? this.confirmationEmailPreference.toJSON()
      : (undefined as any);
    data['exhortationEmailPreference'] = this.exhortationEmailPreference
      ? this.exhortationEmailPreference.toJSON()
      : (undefined as any);
    return data;
  }
}

export interface ICustomerRequest {
  id?: string | undefined;
  code?: number | undefined;
  isActive?: boolean | undefined;
  name: string;
  legalForm?: LegalFormType;
  memo?: string | undefined;
  contactName?: string | undefined;
  salutation?: SalutationType;
  address?: AddressModel;
  correspondenceContactName?: string | undefined;
  correspondenceAddress?: AddressModel;
  phoneNumber?: string | undefined;
  mobileNumber?: string | undefined;
  faxNumber?: string | undefined;
  emailAddressLine?: string[] | undefined;
  website?: string | undefined;
  kvk?: string | undefined;
  vat?: string | undefined;
  oin?: string | undefined;
  fiscalIdentifierType?: FiscalIdentifierType;
  fiscalIdentifier?: string | undefined;
  numberOfFiscalAuthorizations?: number | undefined;
  dateOfBirth?: Date | undefined;
  mainActivityCode?: string | undefined;
  defermentArrangementForFilingTaxReturn?: boolean | undefined;
  paymentPreference?: PaymentPreferenceModel;
  invoiceRelationId?: string | undefined;
  customerGroupId?: string | undefined;
  customerGroup?: CustomerGroupObject;
  salesInvoicePreference?: SalesInvoicePreferenceModel;
  invoiceEmailPreference?: InvoiceEmailPreferenceModel;
  quotationEmailPreference?: EmailPreferenceModel;
  confirmationEmailPreference?: EmailPreferenceModel;
  exhortationEmailPreference?: EmailPreferenceModel;
}

export class CustomerResponse implements ICustomerResponse {
  id?: string | undefined;
  code?: number | undefined;
  isActive?: boolean | undefined;
  name!: string;
  legalForm?: LegalFormType;
  memo?: string | undefined;
  contactName?: string | undefined;
  salutation?: SalutationType;
  address?: AddressModel;
  correspondenceContactName?: string | undefined;
  correspondenceAddress?: AddressModel;
  phoneNumber?: string | undefined;
  mobileNumber?: string | undefined;
  faxNumber?: string | undefined;
  emailAddressLine?: string[] | undefined;
  website?: string | undefined;
  kvk?: string | undefined;
  vat?: string | undefined;
  oin?: string | undefined;
  fiscalIdentifierType?: FiscalIdentifierType;
  fiscalIdentifier?: string | undefined;
  numberOfFiscalAuthorizations?: number | undefined;
  dateOfBirth?: Date | undefined;
  mainActivityCode?: string | undefined;
  defermentArrangementForFilingTaxReturn?: boolean | undefined;
  paymentPreference?: PaymentPreferenceModel;
  invoiceRelationId?: string | undefined;
  customerGroupId?: string | undefined;
  customerGroup?: CustomerGroupObject;
  salesInvoicePreference?: SalesInvoicePreferenceModel;
  invoiceEmailPreference?: InvoiceEmailPreferenceModel;
  quotationEmailPreference?: EmailPreferenceModel;
  confirmationEmailPreference?: EmailPreferenceModel;
  exhortationEmailPreference?: EmailPreferenceModel;

  constructor(data?: ICustomerResponse) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
      this.code = _data['code'];
      this.isActive = _data['isActive'];
      this.name = _data['name'];
      this.legalForm = _data['legalForm'];
      this.memo = _data['memo'];
      this.contactName = _data['contactName'];
      this.salutation = _data['salutation'];
      this.address = _data['address'] ? AddressModel.fromJS(_data['address']) : (undefined as any);
      this.correspondenceContactName = _data['correspondenceContactName'];
      this.correspondenceAddress = _data['correspondenceAddress']
        ? AddressModel.fromJS(_data['correspondenceAddress'])
        : (undefined as any);
      this.phoneNumber = _data['phoneNumber'];
      this.mobileNumber = _data['mobileNumber'];
      this.faxNumber = _data['faxNumber'];
      if (Array.isArray(_data['emailAddressLine'])) {
        this.emailAddressLine = [] as any;
        for (let item of _data['emailAddressLine']) this.emailAddressLine!.push(item);
      }
      this.website = _data['website'];
      this.kvk = _data['kvk'];
      this.vat = _data['vat'];
      this.oin = _data['oin'];
      this.fiscalIdentifierType = _data['fiscalIdentifierType'];
      this.fiscalIdentifier = _data['fiscalIdentifier'];
      this.numberOfFiscalAuthorizations = _data['numberOfFiscalAuthorizations'];
      this.dateOfBirth = _data['dateOfBirth'] ? new Date(_data['dateOfBirth'].toString()) : (undefined as any);
      this.mainActivityCode = _data['mainActivityCode'];
      this.defermentArrangementForFilingTaxReturn = _data['defermentArrangementForFilingTaxReturn'];
      this.paymentPreference = _data['paymentPreference']
        ? PaymentPreferenceModel.fromJS(_data['paymentPreference'])
        : (undefined as any);
      this.invoiceRelationId = _data['invoiceRelationId'];
      this.customerGroupId = _data['customerGroupId'];
      this.customerGroup = _data['customerGroup'] ? CustomerGroupObject.fromJS(_data['customerGroup']) : (undefined as any);
      this.salesInvoicePreference = _data['salesInvoicePreference']
        ? SalesInvoicePreferenceModel.fromJS(_data['salesInvoicePreference'])
        : (undefined as any);
      this.invoiceEmailPreference = _data['invoiceEmailPreference']
        ? InvoiceEmailPreferenceModel.fromJS(_data['invoiceEmailPreference'])
        : (undefined as any);
      this.quotationEmailPreference = _data['quotationEmailPreference']
        ? EmailPreferenceModel.fromJS(_data['quotationEmailPreference'])
        : (undefined as any);
      this.confirmationEmailPreference = _data['confirmationEmailPreference']
        ? EmailPreferenceModel.fromJS(_data['confirmationEmailPreference'])
        : (undefined as any);
      this.exhortationEmailPreference = _data['exhortationEmailPreference']
        ? EmailPreferenceModel.fromJS(_data['exhortationEmailPreference'])
        : (undefined as any);
    }
  }

  static fromJS(data: any): CustomerResponse {
    data = typeof data === 'object' ? data : {};
    let result = new CustomerResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    data['code'] = this.code;
    data['isActive'] = this.isActive;
    data['name'] = this.name;
    data['legalForm'] = this.legalForm;
    data['memo'] = this.memo;
    data['contactName'] = this.contactName;
    data['salutation'] = this.salutation;
    data['address'] = this.address ? this.address.toJSON() : (undefined as any);
    data['correspondenceContactName'] = this.correspondenceContactName;
    data['correspondenceAddress'] = this.correspondenceAddress ? this.correspondenceAddress.toJSON() : (undefined as any);
    data['phoneNumber'] = this.phoneNumber;
    data['mobileNumber'] = this.mobileNumber;
    data['faxNumber'] = this.faxNumber;
    if (Array.isArray(this.emailAddressLine)) {
      data['emailAddressLine'] = [];
      for (let item of this.emailAddressLine) data['emailAddressLine'].push(item);
    }
    data['website'] = this.website;
    data['kvk'] = this.kvk;
    data['vat'] = this.vat;
    data['oin'] = this.oin;
    data['fiscalIdentifierType'] = this.fiscalIdentifierType;
    data['fiscalIdentifier'] = this.fiscalIdentifier;
    data['numberOfFiscalAuthorizations'] = this.numberOfFiscalAuthorizations;
    data['dateOfBirth'] = this.dateOfBirth ? formatDate(this.dateOfBirth) : (undefined as any);
    data['mainActivityCode'] = this.mainActivityCode;
    data['defermentArrangementForFilingTaxReturn'] = this.defermentArrangementForFilingTaxReturn;
    data['paymentPreference'] = this.paymentPreference ? this.paymentPreference.toJSON() : (undefined as any);
    data['invoiceRelationId'] = this.invoiceRelationId;
    data['customerGroupId'] = this.customerGroupId;
    data['customerGroup'] = this.customerGroup ? this.customerGroup.toJSON() : (undefined as any);
    data['salesInvoicePreference'] = this.salesInvoicePreference ? this.salesInvoicePreference.toJSON() : (undefined as any);
    data['invoiceEmailPreference'] = this.invoiceEmailPreference ? this.invoiceEmailPreference.toJSON() : (undefined as any);
    data['quotationEmailPreference'] = this.quotationEmailPreference ? this.quotationEmailPreference.toJSON() : (undefined as any);
    data['confirmationEmailPreference'] = this.confirmationEmailPreference
      ? this.confirmationEmailPreference.toJSON()
      : (undefined as any);
    data['exhortationEmailPreference'] = this.exhortationEmailPreference
      ? this.exhortationEmailPreference.toJSON()
      : (undefined as any);
    return data;
  }
}

export interface ICustomerResponse {
  id?: string | undefined;
  code?: number | undefined;
  isActive?: boolean | undefined;
  name: string;
  legalForm?: LegalFormType;
  memo?: string | undefined;
  contactName?: string | undefined;
  salutation?: SalutationType;
  address?: AddressModel;
  correspondenceContactName?: string | undefined;
  correspondenceAddress?: AddressModel;
  phoneNumber?: string | undefined;
  mobileNumber?: string | undefined;
  faxNumber?: string | undefined;
  emailAddressLine?: string[] | undefined;
  website?: string | undefined;
  kvk?: string | undefined;
  vat?: string | undefined;
  oin?: string | undefined;
  fiscalIdentifierType?: FiscalIdentifierType;
  fiscalIdentifier?: string | undefined;
  numberOfFiscalAuthorizations?: number | undefined;
  dateOfBirth?: Date | undefined;
  mainActivityCode?: string | undefined;
  defermentArrangementForFilingTaxReturn?: boolean | undefined;
  paymentPreference?: PaymentPreferenceModel;
  invoiceRelationId?: string | undefined;
  customerGroupId?: string | undefined;
  customerGroup?: CustomerGroupObject;
  salesInvoicePreference?: SalesInvoicePreferenceModel;
  invoiceEmailPreference?: InvoiceEmailPreferenceModel;
  quotationEmailPreference?: EmailPreferenceModel;
  confirmationEmailPreference?: EmailPreferenceModel;
  exhortationEmailPreference?: EmailPreferenceModel;
}

export class EmailPreferenceModel implements IEmailPreferenceModel {
  isActive?: boolean | undefined;
  emailAddressLine?: string[] | undefined;
  certifiedCopyEmailAddressLine?: string[] | undefined;

  constructor(data?: IEmailPreferenceModel) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isActive = _data['isActive'];
      if (Array.isArray(_data['emailAddressLine'])) {
        this.emailAddressLine = [] as any;
        for (const item of _data['emailAddressLine']) this.emailAddressLine!.push(item);
      }
      if (Array.isArray(_data['certifiedCopyEmailAddressLine'])) {
        this.certifiedCopyEmailAddressLine = [] as any;
        for (const item of _data['certifiedCopyEmailAddressLine']) this.certifiedCopyEmailAddressLine!.push(item);
      }
    }
  }

  static fromJS(data: any): EmailPreferenceModel {
    data = typeof data === 'object' ? data : {};
    const result = new EmailPreferenceModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['isActive'] = this.isActive;
    if (Array.isArray(this.emailAddressLine)) {
      data['emailAddressLine'] = [];
      for (const item of this.emailAddressLine) data['emailAddressLine'].push(item);
    }
    if (Array.isArray(this.certifiedCopyEmailAddressLine)) {
      data['certifiedCopyEmailAddressLine'] = [];
      for (const item of this.certifiedCopyEmailAddressLine) data['certifiedCopyEmailAddressLine'].push(item);
    }
    return data;
  }
}

export interface IEmailPreferenceModel {
  isActive?: boolean | undefined;
  emailAddressLine?: string[] | undefined;
  certifiedCopyEmailAddressLine?: string[] | undefined;
}

export class SalesInvoicePreferenceModel implements ISalesInvoicePreferenceModel {
  paymentTerm?: number | undefined;
  creditLimit?: number | undefined;
  invoiceDiscount?: number | undefined;
  collectionType?: CollectionType;
  exhortationType?: ExhortationType;

  constructor(data?: ISalesInvoicePreferenceModel) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.paymentTerm = _data['paymentTerm'];
      this.creditLimit = _data['creditLimit'];
      this.invoiceDiscount = _data['invoiceDiscount'];
      this.collectionType = _data['collectionType'];
      this.exhortationType = _data['exhortationType'];
    }
  }

  static fromJS(data: any): SalesInvoicePreferenceModel {
    data = typeof data === 'object' ? data : {};
    const result = new SalesInvoicePreferenceModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['paymentTerm'] = this.paymentTerm;
    data['creditLimit'] = this.creditLimit;
    data['invoiceDiscount'] = this.invoiceDiscount;
    data['collectionType'] = this.collectionType;
    data['exhortationType'] = this.exhortationType;
    return data;
  }
}

export class InvoiceEmailPreferenceModel implements IInvoiceEmailPreferenceModel {
  isActive?: boolean | undefined;
  emailAddressLine?: string[] | undefined;
  certifiedCopyEmailAddressLine?: string[] | undefined;
  addUblAttachment?: boolean | undefined;

  constructor(data?: IInvoiceEmailPreferenceModel) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isActive = _data['isActive'];
      if (Array.isArray(_data['emailAddressLine'])) {
        this.emailAddressLine = [] as any;
        for (const item of _data['emailAddressLine']) this.emailAddressLine!.push(item);
      }
      if (Array.isArray(_data['certifiedCopyEmailAddressLine'])) {
        this.certifiedCopyEmailAddressLine = [] as any;
        for (const item of _data['certifiedCopyEmailAddressLine']) this.certifiedCopyEmailAddressLine!.push(item);
      }
      this.addUblAttachment = _data['addUblAttachment'];
    }
  }

  static fromJS(data: any): InvoiceEmailPreferenceModel {
    data = typeof data === 'object' ? data : {};
    const result = new InvoiceEmailPreferenceModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['isActive'] = this.isActive;
    if (Array.isArray(this.emailAddressLine)) {
      data['emailAddressLine'] = [];
      for (const item of this.emailAddressLine) data['emailAddressLine'].push(item);
    }
    if (Array.isArray(this.certifiedCopyEmailAddressLine)) {
      data['certifiedCopyEmailAddressLine'] = [];
      for (const item of this.certifiedCopyEmailAddressLine) data['certifiedCopyEmailAddressLine'].push(item);
    }
    data['addUblAttachment'] = this.addUblAttachment;
    return data;
  }
}

export class HasItemsResult implements IHasItemsResult {
  hasItems?: boolean;

  constructor(data?: IHasItemsResult) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.hasItems = _data['hasItems'];
    }
  }

  static fromJS(data: any): HasItemsResult {
    data = typeof data === 'object' ? data : {};
    const result = new HasItemsResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['hasItems'] = this.hasItems;
    return data;
  }
}

export interface IHasItemsResult {
  hasItems?: boolean;
}

export interface IInvoiceEmailPreferenceModel {
  isActive?: boolean | undefined;
  emailAddressLine?: string[] | undefined;
  certifiedCopyEmailAddressLine?: string[] | undefined;
  addUblAttachment?: boolean | undefined;
}

export interface ISalesInvoicePreferenceModel {
  paymentTerm?: number | undefined;
  creditLimit?: number | undefined;
  invoiceDiscount?: number | undefined;
  collectionType?: CollectionType;
  exhortationType?: ExhortationType;
}

export enum FiscalIdentifierType {
  PersonalIdentity = 'PersonalIdentity',
  LegalEntityAndPartnershipIdentification = 'LegalEntityAndPartnershipIdentification',
}

export enum SalutationType {
  Other = 'Other',
  Sir = 'Sir',
  Madam = 'Madam',
  HeirsOf = 'HeirsOf',
}

export enum LegalFormType {
  Other = 'Other',
  SoleProprietorship = 'SoleProprietorship',
  LimitedPartnership = 'LimitedPartnership',
  ShippingCompany = 'ShippingCompany',
  GeneralPartnership = 'GeneralPartnership',
  Partnership = 'Partnership',
  PrivateLimitedCompany = 'PrivateLimitedCompany',
  Association = 'Association',
  PublicLimitedCompany = 'PublicLimitedCompany',
  Cooperative = 'Cooperative',
  MutualInsuranceCompany = 'MutualInsuranceCompany',
  Foundation = 'Foundation',
  ReligiousDenomination = 'ReligiousDenomination',
}

export enum ExhortationType {
  Unknown = 'Unknown',
  None = 'None',
  Enterprise = 'Enterprise',
  Consumer = 'Consumer',
}

export class PaymentPreferenceModel implements IPaymentPreferenceModel {
  nameOfAccountHolder?: string | undefined;
  locationOfAccountHolder?: string | undefined;
  bankAccountNumber?: string | undefined;
  iban?: string | undefined;
  bic?: string | undefined;
  creditCardNumber?: string | undefined;

  constructor(data?: IPaymentPreferenceModel) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.nameOfAccountHolder = _data['nameOfAccountHolder'];
      this.locationOfAccountHolder = _data['locationOfAccountHolder'];
      this.bankAccountNumber = _data['bankAccountNumber'];
      this.iban = _data['iban'];
      this.bic = _data['bic'];
      this.creditCardNumber = _data['creditCardNumber'];
    }
  }

  static fromJS(data: any): PaymentPreferenceModel {
    data = typeof data === 'object' ? data : {};
    const result = new PaymentPreferenceModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['nameOfAccountHolder'] = this.nameOfAccountHolder;
    data['locationOfAccountHolder'] = this.locationOfAccountHolder;
    data['bankAccountNumber'] = this.bankAccountNumber;
    data['iban'] = this.iban;
    data['bic'] = this.bic;
    data['creditCardNumber'] = this.creditCardNumber;
    return data;
  }
}

export interface IPaymentPreferenceModel {
  nameOfAccountHolder?: string | undefined;
  locationOfAccountHolder?: string | undefined;
  bankAccountNumber?: string | undefined;
  iban?: string | undefined;
  bic?: string | undefined;
  creditCardNumber?: string | undefined;
}

export class AddressModel implements IAddressModel {
  streetName?: string | undefined;
  streetNumber?: number | undefined;
  affix?: string | undefined;
  postalCode?: string | undefined;
  city?: string | undefined;
  countryId?: string | undefined;

  constructor(data?: IAddressModel) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.streetName = _data['streetName'];
      this.streetNumber = _data['streetNumber'];
      this.affix = _data['affix'];
      this.postalCode = _data['postalCode'];
      this.city = _data['city'];
      this.countryId = _data['countryId'];
    }
  }

  static fromJS(data: any): AddressModel {
    data = typeof data === 'object' ? data : {};
    const result = new AddressModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['streetName'] = this.streetName;
    data['streetNumber'] = this.streetNumber;
    data['affix'] = this.affix;
    data['postalCode'] = this.postalCode;
    data['city'] = this.city;
    data['countryId'] = this.countryId;
    return data;
  }
}

export interface IAddressModel {
  streetName?: string | undefined;
  streetNumber?: number | undefined;
  affix?: string | undefined;
  postalCode?: string | undefined;
  city?: string | undefined;
  countryId?: string | undefined;
}

export class CustomersResponse implements ICustomersResponse {
  customers?: CustomerResponse[] | undefined;

  constructor(data?: ICustomersResponse) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['customers'])) {
        this.customers = [] as any;
        for (const item of _data['customers']) this.customers!.push(CustomerResponse.fromJS(item));
      }
    }
  }

  static fromJS(data: any): CustomersResponse {
    data = typeof data === 'object' ? data : {};
    const result = new CustomersResponse();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.customers)) {
      data['customers'] = [];
      for (const item of this.customers) data['customers'].push(item.toJSON());
    }
    return data;
  }
}

export interface ICustomersResponse {
  customers?: CustomerResponse[] | undefined;
}

export interface IRelationColumnDateFilter {
  columnName: RelationColumn;
  fromDate?: Date | undefined;
  tillDate?: Date | undefined;
}

export interface IRelationColumnNumericFilter {
  columnName: RelationColumn;
  type: NumericFilterType;
  value?: number | undefined;
  lowerBound?: number | undefined;
  upperBound?: number | undefined;
}

export enum NumericFilterType {
  Between = 'Between',
  GreaterThanOrEqualTo = 'GreaterThanOrEqualTo',
  LessThanOrEqualTo = 'LessThanOrEqualTo',
  EqualTo = 'EqualTo',
}

export class RelationColumnAlphaNumericFilter implements IRelationColumnAlphaNumericFilter {
  columnName!: RelationColumn;
  type!: AlphaNumericFilterType;
  value!: string;

  constructor(data?: IRelationColumnAlphaNumericFilter) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.columnName = _data['columnName'];
      this.type = _data['type'];
      this.value = _data['value'];
    }
  }

  static fromJS(data: any): RelationColumnAlphaNumericFilter {
    data = typeof data === 'object' ? data : {};
    const result = new RelationColumnAlphaNumericFilter();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['columnName'] = this.columnName;
    data['type'] = this.type;
    data['value'] = this.value;
    return data;
  }
}

export enum AlphaNumericFilterType {
  EqualTo = 'EqualTo',
  StartsWith = 'StartsWith',
  Contains = 'Contains',
  EndsWith = 'EndsWith',
}

export interface IRelationColumnAlphaNumericFilter {
  columnName: RelationColumn;
  type: AlphaNumericFilterType;
  value: string;
}

export interface IRelationColumnAlphaNumericFilter {
  columnName: RelationColumn;
  type: AlphaNumericFilterType;
  value: string;
}

export class RelationColumnNumericFilter implements IRelationColumnNumericFilter {
  columnName!: RelationColumn;
  type!: NumericFilterType;
  value?: number | undefined;
  lowerBound?: number | undefined;
  upperBound?: number | undefined;

  constructor(data?: IRelationColumnNumericFilter) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.columnName = _data['columnName'];
      this.type = _data['type'];
      this.value = _data['value'];
      this.lowerBound = _data['lowerBound'];
      this.upperBound = _data['upperBound'];
    }
  }

  static fromJS(data: any): RelationColumnNumericFilter {
    data = typeof data === 'object' ? data : {};
    const result = new RelationColumnNumericFilter();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['columnName'] = this.columnName;
    data['type'] = this.type;
    data['value'] = this.value;
    data['lowerBound'] = this.lowerBound;
    data['upperBound'] = this.upperBound;
    return data;
  }
}

export class RelationColumnColumnFilters implements IRelationColumnColumnFilters {
  alphaNumeric?: RelationColumnAlphaNumericFilter[] | undefined;
  numeric?: RelationColumnNumericFilter[] | undefined;
  date?: RelationColumnDateFilter[] | undefined;

  constructor(data?: IRelationColumnColumnFilters) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['alphaNumeric'])) {
        this.alphaNumeric = [] as any;
        for (const item of _data['alphaNumeric']) this.alphaNumeric!.push(RelationColumnAlphaNumericFilter.fromJS(item));
      }
      if (Array.isArray(_data['numeric'])) {
        this.numeric = [] as any;
        for (const item of _data['numeric']) this.numeric!.push(RelationColumnNumericFilter.fromJS(item));
      }
      if (Array.isArray(_data['date'])) {
        this.date = [] as any;
        for (const item of _data['date']) this.date!.push(RelationColumnDateFilter.fromJS(item));
      }
    }
  }

  static fromJS(data: any): RelationColumnColumnFilters {
    data = typeof data === 'object' ? data : {};
    const result = new RelationColumnColumnFilters();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.alphaNumeric)) {
      data['alphaNumeric'] = [];
      for (const item of this.alphaNumeric) data['alphaNumeric'].push(item.toJSON());
    }
    if (Array.isArray(this.numeric)) {
      data['numeric'] = [];
      for (const item of this.numeric) data['numeric'].push(item.toJSON());
    }
    if (Array.isArray(this.date)) {
      data['date'] = [];
      for (const item of this.date) data['date'].push(item.toJSON());
    }
    return data;
  }
}

export class RelationColumnPaginationRequest implements IRelationColumnPaginationRequest {
  searchTerm?: string | undefined;
  /** Page number, starting at 0 */
  page!: number;
  /** Page size, can be between 0 and 100.
   A size of 0 is useful when you only want to return the count. */
  pageSize!: number;
  sortByColumn?: RelationColumn;
  sortDirection?: SortDirection;
  /** Represents the translations for the enum values being sorted on. */
  sortEnumTranslations?: EnumTranslation[] | undefined;
  filters?: RelationColumnColumnFilters;

  constructor(data?: IRelationColumnPaginationRequest) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.searchTerm = _data['searchTerm'];
      this.page = _data['page'];
      this.pageSize = _data['pageSize'];
      this.sortByColumn = _data['sortByColumn'];
      this.sortDirection = _data['sortDirection'];
      if (Array.isArray(_data['sortEnumTranslations'])) {
        this.sortEnumTranslations = [] as any;
        for (const item of _data['sortEnumTranslations']) this.sortEnumTranslations!.push(EnumTranslation.fromJS(item));
      }
      this.filters = _data['filters'] ? RelationColumnColumnFilters.fromJS(_data['filters']) : (undefined as any);
    }
  }

  static fromJS(data: any): RelationColumnPaginationRequest {
    data = typeof data === 'object' ? data : {};
    const result = new RelationColumnPaginationRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['searchTerm'] = this.searchTerm;
    data['page'] = this.page;
    data['pageSize'] = this.pageSize;
    data['sortByColumn'] = this.sortByColumn;
    data['sortDirection'] = this.sortDirection;
    if (Array.isArray(this.sortEnumTranslations)) {
      data['sortEnumTranslations'] = [];
      for (const item of this.sortEnumTranslations) data['sortEnumTranslations'].push(item.toJSON());
    }
    data['filters'] = this.filters ? this.filters.toJSON() : (undefined as any);
    return data;
  }
}

export interface ApiErrorDetail {
  global?: [
    {
      errorCode?: string;
      message?: string;
      instanceId?: string | null;
    },
  ];
  field?: string;
}

export interface ApiErrors {
  logGroup?: string;
  title?: string;
  traceId?: string;
  status?: number;
  instance?: string;
  detail?: string;
  errors?: ApiErrorDetail;
  field?: Record<string, string[]>;
}

export interface HttpError {
  headers: {
    normalizedNames: string;
    lazyUpdate: string;
  };
  ok: boolean;
  statusText: string;
  message?: string;
  status?: number;
  error?: ApiErrors;
}

export interface OphaalResponse {
  inhoud: string;
  berichtsoort: string;
}

export interface DagBoekType {
  dagboekType: string;
  id: number;
  publicIdentifier: string;
  number: number;
  description: string;
  boekstukAutomatisch: boolean;
  outOfUse: boolean;
}

interface ContactGegevens {
  companyName: string;
  kvkNumber: string;
  contactpersoon: ContactPersoon;
  vestigingsadres: VestigingsAdres;
  factuuradres: string;
}

interface ContactPersoon {
  gender: number;
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
}

export interface VestigingsAdres {
  rawAdres: string;
  straatnaam: string;
  huisnummer: number;
  huisnummerToevoeging: string;
  plaats: string;
  postCode: string;
  landCode: string;
  email: string;
  isAfwijkendFactuurAdres: boolean;
}

export interface CompanyInfo {
  id: string;
  name: string;
  administrationId: string;
  isConfirmed: boolean;
  nieuwsbrief: boolean;
  contactgegevens: ContactGegevens;
  abonnement: string;
  betaalgegevens: string;
  heeftRechtOpService: boolean;
  isBetaTestgroepDeelnemer: boolean;
  accountType: number;
}

interface AdministrationSettings {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  mobilePhoneNumber: string;
  fax: string;
  streetName: string;
  streetNumber: string;
  affix: string;
  postalCode: string;
  city: string;
  country: string;
  website: string;
  legalForm: string;
  btwNumber: string;
  identificationNumber: string;
  kvk: string;
  bankNumber: string;
  iban: string;
  bic: string;
  autoGrantAccess: boolean;
}

export interface IRelationSearchResults {
  results: RelationSearchResult[];
  total: number;
}

export class RelationSearchResults implements IRelationSearchResults {
  results!: RelationSearchResult[];
  total!: number;

  constructor(data?: IRelationSearchResults) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
    if (!data) {
      this.results = [];
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data['results'])) {
        this.results = [] as any;
        for (const item of _data['results']) this.results!.push(RelationSearchResult.fromJS(item));
      }
      this.total = _data['total'];
    }
  }

  static fromJS(data: any): RelationSearchResults {
    data = typeof data === 'object' ? data : {};
    const result = new RelationSearchResults();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    if (Array.isArray(this.results)) {
      data['results'] = [];
      for (const item of this.results) data['results'].push(item.toJSON());
    }
    data['total'] = this.total;
    return data;
  }
}

export class RelationSearchResult implements IRelationSearchResult {
  id!: string;
  code!: number;
  name!: string;
  chamberOfCommerceNumber?: string | undefined;
  legalForm?: LegalFormType;
  mainActivityCode?: string | undefined;
  valueAddedTaxNumber?: string | undefined;
  organizationIdentificationNumber?: string | undefined;
  contactPerson?: string | undefined;
  salutation?: SalutationType;
  addressLine?: string | undefined;
  postalCode?: string | undefined;
  city?: string | undefined;
  country?: string;
  dateOfBirth?: Date | undefined;
  phoneNumber?: string | undefined;
  mobilePhoneNumber?: string | undefined;
  email?: string | undefined;
  iban?: string | undefined;
  bic?: string | undefined;
  fiscalIdentifierType?: FiscalIdentifierType;
  fiscalIdentifier?: string | undefined;
  numberOfFiscalAuthorizations?: number | undefined;
  defermentArrangementForFilingTaxReturn?: boolean | undefined;

  constructor(data?: IRelationSearchResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data['id'];
      this.code = _data['code'];
      this.name = _data['name'];
      this.chamberOfCommerceNumber = _data['chamberOfCommerceNumber'];
      this.legalForm = _data['legalForm'];
      this.mainActivityCode = _data['mainActivityCode'];
      this.valueAddedTaxNumber = _data['valueAddedTaxNumber'];
      this.organizationIdentificationNumber = _data['organizationIdentificationNumber'];
      this.contactPerson = _data['contactPerson'];
      this.salutation = _data['salutation'];
      this.addressLine = _data['addressLine'];
      this.postalCode = _data['postalCode'];
      this.city = _data['city'];
      this.country = _data['country'];
      this.dateOfBirth = _data['dateOfBirth'] ? new Date(_data['dateOfBirth'].toString()) : (undefined as any);
      this.phoneNumber = _data['phoneNumber'];
      this.mobilePhoneNumber = _data['mobilePhoneNumber'];
      this.email = _data['email'];
      this.iban = _data['iban'];
      this.bic = _data['bic'];
      this.fiscalIdentifierType = _data['fiscalIdentifierType'];
      this.fiscalIdentifier = _data['fiscalIdentifier'];
      this.numberOfFiscalAuthorizations = _data['numberOfFiscalAuthorizations'];
      this.defermentArrangementForFilingTaxReturn = _data['defermentArrangementForFilingTaxReturn'];
    }
  }

  static fromJS(data: any): RelationSearchResult {
    data = typeof data === 'object' ? data : {};
    let result = new RelationSearchResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['id'] = this.id;
    data['code'] = this.code;
    data['name'] = this.name;
    data['chamberOfCommerceNumber'] = this.chamberOfCommerceNumber;
    data['legalForm'] = this.legalForm;
    data['mainActivityCode'] = this.mainActivityCode;
    data['valueAddedTaxNumber'] = this.valueAddedTaxNumber;
    data['organizationIdentificationNumber'] = this.organizationIdentificationNumber;
    data['contactPerson'] = this.contactPerson;
    data['salutation'] = this.salutation;
    data['addressLine'] = this.addressLine;
    data['postalCode'] = this.postalCode;
    data['city'] = this.city;
    data['country'] = this.country;
    data['dateOfBirth'] = this.dateOfBirth ? formatDate(this.dateOfBirth) : (undefined as any);
    data['phoneNumber'] = this.phoneNumber;
    data['mobilePhoneNumber'] = this.mobilePhoneNumber;
    data['email'] = this.email;
    data['iban'] = this.iban;
    data['bic'] = this.bic;
    data['fiscalIdentifierType'] = this.fiscalIdentifierType;
    data['fiscalIdentifier'] = this.fiscalIdentifier;
    data['numberOfFiscalAuthorizations'] = this.numberOfFiscalAuthorizations;
    data['defermentArrangementForFilingTaxReturn'] = this.defermentArrangementForFilingTaxReturn;
    return data;
  }
}

export interface IRelationSearchResult {
  id: string;
  code: number;
  name: string;
  chamberOfCommerceNumber?: string | undefined;
  legalForm?: LegalFormType;
  mainActivityCode?: string | undefined;
  valueAddedTaxNumber?: string | undefined;
  organizationIdentificationNumber?: string | undefined;
  contactPerson?: string | undefined;
  salutation?: SalutationType;
  addressLine?: string | undefined;
  postalCode?: string | undefined;
  city?: string | undefined;
  country?: string;
  dateOfBirth?: Date | undefined;
  phoneNumber?: string | undefined;
  mobilePhoneNumber?: string | undefined;
  email?: string | undefined;
  iban?: string | undefined;
  bic?: string | undefined;
  fiscalIdentifierType?: FiscalIdentifierType;
  fiscalIdentifier?: string | undefined;
  numberOfFiscalAuthorizations?: number | undefined;
  defermentArrangementForFilingTaxReturn?: boolean | undefined;
}

export interface IRelationSearchResults {
  results: RelationSearchResult[];
  total: number;
}

export interface AdministrationSettingsQueryResult {
  administrationSettings: AdministrationSettings;
}

export interface IKolommenbalansResponse {
  getKolommembalansResponseResults?: GetKolommenbalansResponseResult[] | undefined;
  kolommenbalansTotalResponseResult?: KolommenbalansTotalResponseResult | undefined;
}

export class KolommenbalansTotalResponseResult implements IKolommenbalansTotalResponseResult {
  totalDebetProefSaldiBalans?: number;
  totalCreditProefSaldiBalans?: number;
  totalDebetWinstenverlies?: number;
  totalCreditWinstenverlies?: number;
  totalDebetBalans?: number;
  totalCreditBalans?: number;
  resultDebetWinstenverlies?: number;
  resultCreditWinstenverlies?: number;
  resultDebetBalans?: number;
  resultCreditBalans?: number;
  totalResultDebetWinstenverlies?: number;
  totalResultCreditWinstenverlies?: number;
  totalResultDebetBalans?: number;
  totalResultCreditBalans?: number;

  constructor(data?: IKolommenbalansTotalResponseResult) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.totalDebetProefSaldiBalans = _data['totalDebetProefSaldiBalans'];
      this.totalCreditProefSaldiBalans = _data['totalCreditProefSaldiBalans'];
      this.totalDebetWinstenverlies = _data['totalDebetWinstenverlies'];
      this.totalCreditWinstenverlies = _data['totalCreditWinstenverlies'];
      this.totalDebetBalans = _data['totalDebetBalans'];
      this.totalCreditBalans = _data['totalCreditBalans'];
      this.resultDebetWinstenverlies = _data['resultDebetWinstenverlies'];
      this.resultCreditWinstenverlies = _data['resultCreditWinstenverlies'];
      this.resultDebetBalans = _data['resultDebetBalans'];
      this.resultCreditBalans = _data['resultCreditBalans'];
      this.totalResultDebetWinstenverlies = _data['totalResultDebetWinstenverlies'];
      this.totalResultCreditWinstenverlies = _data['totalResultCreditWinstenverlies'];
      this.totalResultDebetBalans = _data['totalResultDebetBalans'];
      this.totalResultCreditBalans = _data['totalResultCreditBalans'];
    }
  }

  static fromJS(data: any): KolommenbalansTotalResponseResult {
    data = typeof data === 'object' ? data : {};
    const result = new KolommenbalansTotalResponseResult();
    result.init(data);
    return result;
  }
}

export interface IKolommenbalansTotalResponseResult {
  totalDebetProefSaldiBalans?: number;
  totalCreditProefSaldiBalans?: number;
  totalDebetWinstenverlies?: number;
  totalCreditWinstenverlies?: number;
  totalDebetBalans?: number;
  totalCreditBalans?: number;
  resultDebetWinstenverlies?: number;
  resultCreditWinstenverlies?: number;
  resultDebetBalans?: number;
  resultCreditBalans?: number;
  totalResultDebetWinstenverlies?: number;
  totalResultCreditWinstenverlies?: number;
  totalResultDebetBalans?: number;
  totalResultCreditBalans?: number;
}

export class GetKolommenbalansResponseResult implements IGetKolommenbalansResponseResult {
  publicIdentifier?: string;
  balans?: string | undefined;
  hoofdGrootboekRubriek?: string | undefined;
  rekeningCode?: number;
  rubriekId?: number | undefined;
  nummer?: number;
  omschrijving?: string | undefined;
  debetWinstenverlies?: number;
  creditWinstenverlies?: number;
  debetBalans?: number;
  creditBalans?: number;
  debetProefSaldiBalans?: number;
  creditProefSaldiBalans?: number;

  constructor(data?: IGetKolommenbalansResponseResult) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) (this as any)[property] = (data as any)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.publicIdentifier = _data['publicIdentifier'];
      this.balans = _data['balans'];
      this.hoofdGrootboekRubriek = _data['hoofdGrootboekRubriek'];
      this.rekeningCode = _data['rekeningCode'];
      this.rubriekId = _data['rubriekId'];
      this.nummer = _data['nummer'];
      this.omschrijving = _data['omschrijving'];
      this.debetWinstenverlies = _data['debetWinstenverlies'];
      this.creditWinstenverlies = _data['creditWinstenverlies'];
      this.debetBalans = _data['debetBalans'];
      this.creditBalans = _data['creditBalans'];
      this.debetProefSaldiBalans = _data['debetProefSaldiBalans'];
      this.creditProefSaldiBalans = _data['creditProefSaldiBalans'];
    }
  }

  static fromJS(data: any): GetKolommenbalansResponseResult {
    data = typeof data === 'object' ? data : {};
    const result = new GetKolommenbalansResponseResult();
    result.init(data);
    return result;
  }
}

export interface IGetKolommenbalansResponseResult {
  publicIdentifier?: string;
  balans?: string | undefined;
  hoofdGrootboekRubriek?: string | undefined;
  rekeningCode?: number;
  rubriekId?: number | undefined;
  nummer?: number;
  omschrijving?: string | undefined;
  debetWinstenverlies?: number;
  creditWinstenverlies?: number;
  debetBalans?: number;
  creditBalans?: number;
  debetProefSaldiBalans?: number;
  creditProefSaldiBalans?: number;
}

export interface IAlphaNumericFilterModel {
  columnName?: string | undefined;
  value?: string | undefined;
}

export interface UitlegResponse {
  aangever: UitlegVelden;
  partner: UitlegVelden;
}

export interface UitlegVelden {
  bedragAanslag?: number;
  nogVerschuldigdOverBox1Looninkomen?: number;
  nogVerschuldigdOverBox1Winst?: number;
  effectOuderenkortingBox1Inkomen?: number;
  effectAftrekpostenInclAanslaggrens?: number;
  effectPositiefInkomenEigenWoning?: number;
  effectOuderenkortingAftrekposten?: number;
  nogTeBetalenOverBox2?: number;
  effectOuderenkortingBox2?: number;
  nogTeBetalenOverBox3?: number;
  effectOuderenkortingBox3?: number;
  alOpgelegdeVoorlopigeAanslag?: number;
  dividendbelasting?: number;
  effectRuimteLoonheffingskorting?: number;
  kortinggroenebeleggingen?: number;
  inkomensafhankelijkecombinatiekorting?: number;
  verhogingVanwegePartner?: number;
  onbenutteEigenHeffingskorting?: number;
  maximeringHeffingskorting?: number;
  aanslaggrens?: number;
  verzamelpost?: number;
  isVerklaard?: boolean;
  verschil?: number;
}

export interface ControleRequest {
  aangifte: Aangifte;
  aangifteVorigJaar?: Aangifte;
  viaAangever?: Aangifte;
  viaPartner?: Aangifte;

  [key: string]: any;
}

export interface ControleResponse {
  bevindingList?: Bevinding[];

  [key: string]: any;
}

export interface Bevinding {
  id?: string;
  title: string;
  categorie?: string;
  type?: string;
  bevinding: string;
  actie: string;
  persoon?: string;

  [key: string]: any;
}

export interface ErrorMessage {
  error?: string;
  message?: string;

  [key: string]: any;
}

export interface AdministratieApiSst {
  taxDeclaration: {
    lastStatusChangeOn: string;
    status: string;
    period: string;
    endDateOfPeriod: string;
    reason: string;
  };
  bookings: {
    numberOfMarkedPurchaseBookings: number;
    numberOfMarkedBankBookings: number;
    numberOfQueryPosts: number;
  };
  periodLock: {
    activePeriodLockUntil: string;
  };
  bankDifferenceData: {
    missingBankDifferences: [];
  };
  rgs: {
    rgsStatus: string;
  };
  lastRefreshedOn: string;
  numberOfBankAccountsWithProblems: number;
  id: string;
  name: string;
  type: number;
  status: number;
  lastOpened: string;
  lastOpenedBy: string;
  owner: string;
  isDiva: boolean;
}

function throwException(message: string, status: number, response: string, headers: Record<string, any>, result?: any): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else return _observableThrow(new SwaggerException(message, status, response, headers, null));
}

class SwaggerException extends Error {
  override message: string;
  status: number;
  response: string;
  headers: Record<string, any>;
  result: any;

  constructor(message: string, status: number, response: string, headers: Record<string, any>, result: any) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isSwaggerException = true;

  static isSwaggerException(obj: any): obj is SwaggerException {
    return obj.isSwaggerException === true;
  }
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next('');
      observer.complete();
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

function formatDate(d: Date) {
  return (
    d.getFullYear() +
    '-' +
    (d.getMonth() < 9 ? '0' + (d.getMonth() + 1) : d.getMonth() + 1) +
    '-' +
    (d.getDate() < 10 ? '0' + d.getDate() : d.getDate())
  );
}

export const relationTypeToDutchTranslationsMap: Record<RelationType, string> = {
  [RelationType.Customer]: $localize`Klant`,
  [RelationType.Supplier]: $localize`Leverancier`,
  [RelationType.Unspecified]: $localize`Geen`,
  [RelationType.AdministrationOwner]: $localize`Eigen`,
};

export const relationTypeToPluralDutchTranslationsMap: Record<RelationType, string> = {
  [RelationType.Customer]: $localize`Klanten`,
  [RelationType.Supplier]: $localize`Leveranciers`,
  [RelationType.Unspecified]: $localize`Geen`,
  [RelationType.AdministrationOwner]: $localize`Eigen`,
};

export function catchAdministrationNotMigratedError<T, O extends ObservableInput<T>>(
  toastService: ToastService,
): OperatorFunction<T, T | ObservedValueOf<O>> {
  return catchError((error) => {
    if (error instanceof ProblemDetails && error.detail === 'Invalid column name \u0027PublicIdentifier\u0027.') {
      toastService.showErrorMessage(
        $localize`Open deze administratie één keer in SnelStart Web (https://web.snelstart.nl) om deze automatisch bij te werken voor SnelStart Polaris.`,
      );
    }
    return throwError(() => error);
  });
}

export class OpenRelationDetailsEvent extends SnelStartEvent<{ id?: string; type: RelationType }> {
  constructor() {
    super('product-relation-management-open-relation-details-event');
  }
}
